import { CalculatorConfig, InputConfig } from "../country/types";

const retirementAccountInputs = [
  { id: "principal", name: "Current Balance", type: "currency", defaultValue: 5000, min: 0 },
  { id: "annualRate", name: "Expected Annual Return", type: "percent", defaultValue: 8.0, min: 0, max: 15 },
  { id: "termYears", name: "Years Until Retirement", type: "number", defaultValue: 30, min: 1 },
  { id: "monthlyContribution", name: "Monthly Contribution", type: "currency", defaultValue: 500, min: 0 }
] as const;

const retirementAccountOutputs = [
  { id: "endBalance", name: "Projected Balance", type: "currency" },
  { id: "totalContributions", name: "Total Principal Invested", type: "currency" },
  { id: "totalInterest", name: "Total Compounded Growth", type: "currency" }
] as const;

const debtPayoffInputs: InputConfig[] = [
  { id: "strategy", name: "Repayment Strategy", type: "select", defaultValue: "snowball", options: [{ label: "Debt Snowball (Smallest Balance)", value: "snowball" }, { label: "Debt Avalanche (Highest Interest)", value: "avalanche" }] },
  { id: "extraBudget", name: "Extra Monthly Payoff Budget", type: "currency", defaultValue: 300, min: 0 },
  { id: "debt1Balance", name: "Debt 1 Balance", type: "currency", defaultValue: 5000, min: 0 },
  { id: "debt1Rate", name: "Debt 1 Interest Rate (%)", type: "percent", defaultValue: 18.0, min: 0 },
  { id: "debt1Min", name: "Debt 1 Minimum Payment", type: "currency", defaultValue: 100, min: 0 },
  { id: "debt2Balance", name: "Debt 2 Balance", type: "currency", defaultValue: 10000, min: 0 },
  { id: "debt2Rate", name: "Debt 2 Interest Rate (%)", type: "percent", defaultValue: 8.0, min: 0 },
  { id: "debt2Min", name: "Debt 2 Minimum Payment", type: "currency", defaultValue: 200, min: 0 },
  { id: "debt3Balance", name: "Debt 3 Balance", type: "currency", defaultValue: 15000, min: 0 },
  { id: "debt3Rate", name: "Debt 3 Interest Rate (%)", type: "percent", defaultValue: 5.0, min: 0 },
  { id: "debt3Min", name: "Debt 3 Minimum Payment", type: "currency", defaultValue: 300, min: 0 }
];

const filingStatusInput: InputConfig = {
  id: "filingStatus",
  name: "Filing Status",
  type: "select",
  defaultValue: "single",
  options: [
    { label: "Single", value: "single" },
    { label: "Married Filing Jointly", value: "married_jointly" },
    { label: "Head of Household", value: "head_of_household" }
  ]
};

const magiInput: InputConfig = {
  id: "magi",
  name: "Modified AGI (MAGI)",
  type: "currency",
  defaultValue: 50000,
  min: 0
};

const baseCalculatorRegistry: CalculatorConfig[] = [
  {
    id: "california-ab1847-wildfire-forbearance-planner",
    name: "California AB 1847 Wildfire Forbearance Planner",
    slug: "california-ab1847-wildfire-forbearance-planner",
    category: "loans",
    description: "Plan remaining months under California AB 1847's 24-month LA wildfire mortgage forbearance cap and illustrate payments postponed, not forgiven.",
    countries: ["us"],
    inputs: [
      {
        id: "priorForbearanceMonths",
        name: "Prior wildfire forbearance months counted by servicer",
        type: "number",
        defaultValue: 10,
        min: 0,
        max: 24,
        step: 1,
        tooltip: "Use the months your servicer counts toward the cap, including earlier forbearance for the same 2025 LA wildfire disaster. The servicer must confirm exact dates; do not turn 90 days into three calendar months yourself."
      },
      {
        id: "requestedAdditionalMonths",
        name: "Additional months to model",
        type: "number",
        defaultValue: 12,
        min: 0,
        max: 24,
        step: 1,
        tooltip: "This is a planning span, not a forbearance request. The law starts with up to 90 days and later extensions require separate requests in 90-day increments."
      },
      {
        id: "monthlyMortgagePayment",
        name: "Current monthly mortgage payment ($)",
        type: "currency",
        defaultValue: 2500,
        min: 0,
        max: 50000,
        tooltip: "Enter the payment you would otherwise make each month. This illustrative cash-flow total is deferred and still owed; it is not forgiveness or a grant."
      }
    ],
    outputs: [
      { id: "remainingBeforeRequest", name: "Cap room before proposed period (months)", type: "number" },
      { id: "monthsWithinCap", name: "Proposed months within 24-month cap", type: "number" },
      { id: "monthsBeyondCap", name: "Proposed months beyond cap", type: "number" },
      { id: "remainingAfterRequest", name: "Cap room after modeled period (months)", type: "number" },
      { id: "illustrativePausedPayments", name: "Illustrative payments paused, still owed ($)", type: "currency" },
      { id: "approvalNotice", name: "Approval and 90-day request notice", type: "text" },
      { id: "documentationNotice", name: "Beyond-12-month documentation rule", type: "text" },
      { id: "repaymentNotice", name: "Repayment and investor caveat", type: "text" }
    ],
    formula: "calculateCaliforniaWildfireForbearance"
  },
  {
    id: "au-pension-supplement-overseas-12-week-calculator",
    name: "Australia Pension Supplement Overseas Travel Calculator",
    slug: "au-pension-supplement-overseas-12-week-calculator",
    category: "retirement",
    description: "Estimate Pension Supplement cash flow on an overseas trip under Australia's 12-week rule from 20 September 2026, using your actual fortnightly amount.",
    countries: ["au"],
    inputs: [
      {
        id: "fortnightlySupplement",
        name: "Your Pension Supplement per fortnight (A$)",
        type: "currency",
        defaultValue: 56,
        min: 0,
        max: 200,
        tooltip: "A$56 is an illustration only. Use your full fortnightly Pension Supplement entitlement, including any part you chose to receive quarterly. Ask Services Australia or DVA for that equivalent if your statement shows only a partial fortnightly amount. Exclude the main pension and separately listed Energy Supplement."
      },
      {
        id: "daysOverseas",
        name: "Days outside Australia",
        type: "number",
        defaultValue: 98,
        min: 0,
        max: 365,
        tooltip: "Count planned days overseas for a trip beginning on or after 20 September 2026. The payment agency decides exact departure and return day treatment."
      },
      {
        id: "mainPaymentPayableDays",
        name: "Days your main pension remains payable abroad",
        type: "number",
        defaultValue: 98,
        min: 0,
        max: 365,
        tooltip: "The sample matches the 98-day trip. Enter the duration Services Australia or DVA confirms for your main payment; it can be shorter than 12 weeks for some payments."
      },
      {
        id: "travelType",
        name: "Purpose of departure",
        type: "select",
        defaultValue: "temporary",
        options: [
          { label: "Temporary trip; Australia remains home", value: "temporary" },
          { label: "Moving overseas to live", value: "permanent" }
        ]
      }
    ],
    outputs: [
      { id: "payableDays", name: "Estimated days with Pension Supplement", type: "number" },
      { id: "supplementDuringTrip", name: "Estimated Pension Supplement during trip (A$)", type: "currency" },
      { id: "unpaidDays", name: "Days abroad without the supplement", type: "number" },
      { id: "supplementNotPaid", name: "Supplement not paid at entered rate (A$)", type: "currency" },
      { id: "dailySupplement", name: "Daily equivalent of entered supplement (A$)", type: "currency" },
      { id: "travelNotice", name: "Overseas payment rule", type: "text" }
    ],
    formula: "calculateAuOverseasPensionSupplement"
  },
  {
    id: "nz-fees-free-2026-completion-cutoff-calculator",
    name: "NZ Fees Free 2026 Completion Cutoff Calculator",
    slug: "nz-fees-free-2026-completion-cutoff-calculator",
    category: "loans",
    description: "Estimate the conditional Fees Free amount exposed when a New Zealand qualification finishes after 31 December 2026.",
    countries: ["nz"],
    inputs: [
      {
        id: "completionTiming",
        name: "When will you meet the qualification requirements?",
        type: "select",
        defaultValue: "by_2026_12_31",
        options: [
          { label: "By 31 December 2026", value: "by_2026_12_31" },
          { label: "1 January 2027 or later", value: "from_2027_01_01" }
        ]
      },
      {
        id: "qualifyingFees",
        name: "Final-year eligible fees within the 1 EFTS or 24-month limit (NZ$)",
        type: "currency",
        defaultValue: 8600,
        min: 0,
        max: 50000,
        tooltip: "Use tuition, compulsory course costs and student services fees for up to 1 EFTS of provider study, or eligible training fees for up to 24 months of work-based learning. TEC determines the final eligible amount."
      },
      {
        id: "otherEligibilityMet",
        name: "Do you meet the other IRD and TEC eligibility rules?",
        type: "select",
        defaultValue: "yes",
        options: [
          { label: "Yes, based on myIR and TEC guidance", value: "yes" },
          { label: "No or not confirmed", value: "no" }
        ]
      },
      {
        id: "previousFeesFree",
        name: "Have you already received first-year Fees Free?",
        type: "select",
        defaultValue: "no",
        options: [
          { label: "No", value: "no" },
          { label: "Yes", value: "yes" }
        ]
      }
    ],
    outputs: [
      { id: "supportIfOnTime", name: "Conditional support if completed by cutoff (NZ$)", type: "currency" },
      { id: "supportUnderSelectedTiming", name: "Conditional support with selected completion timing (NZ$)", type: "currency" },
      { id: "amountLostAfterCutoff", name: "Conditional support lost to 2027 completion (NZ$)", type: "currency" },
      { id: "completionStatus", name: "Cutoff result and limits", type: "text" },
      { id: "applicationNote", name: "Claim timing", type: "text" }
    ],
    formula: "calculateNzFeesFreeCutoff"
  },
  {
    id: "fma-cccfa-complaint-eligibility-checker-july-2026",
    name: "NZ FMA Credit Complaint Route and Charges Checker",
    slug: "fma-cccfa-complaint-eligibility-checker-july-2026",
    category: "loans",
    description: "Find the current FMA and dispute-scheme complaint route for an old or new consumer loan, and tally actual interest and fees from your statements without guessing a payout.",
    countries: ["nz"],
    inputs: [
      {
        id: "loanOriginationDate",
        name: "When did the loan start?",
        type: "select",
        defaultValue: "post_july_2026",
        options: [
          { label: "On or after 1 July 2026", value: "post_july_2026" },
          { label: "Before 1 July 2026", value: "pre_july_2026" }
        ]
      },
      {
        id: "lenderType",
        name: "Lender Entity Classification",
        type: "select",
        defaultValue: "registered_bank",
        options: [
          { label: "Registered Bank", value: "registered_bank" },
          { label: "Non-Bank Financial Institution / Finance Co", value: "nbfc_finance_company" },
          { label: "Private or P2P lender (business status to check)", value: "unregulated_p2p" }
        ]
      },
      {
        id: "issueType",
        name: "Primary Conduct / Loan Grievance Issue",
        type: "select",
        defaultValue: "predatory_irresponsible_lending",
        options: [
          { label: "Predatory / Irresponsible Lending (Affordability Failure)", value: "predatory_irresponsible_lending" },
          { label: "Unreasonable Credit Fees & Penalty Interest", value: "unreasonable_fees" },
          { label: "Harassment & Predatory Debt Collection", value: "harassment_collection" },
          { label: "Lender Certification & Licensing Failure", value: "lender_licensing_failure" }
        ]
      },
      { id: "interestPaid", name: "Interest actually paid on statements (NZ$)", type: "currency", defaultValue: 4000, min: 0, max: 1000000000, tooltip: "Enter paid interest from statements. This is evidence to review, not a presumed refund." },
      { id: "feesPaid", name: "Loan and default fees actually paid (NZ$)", type: "currency", defaultValue: 500, min: 0, max: 1000000000, tooltip: "Enter fees actually paid. A dispute scheme or court must decide whether any fee is unlawful or refundable." },
      {
        id: "hasFmaLicense",
        name: "Have you verified the lender on the FSPR?",
        type: "select",
        defaultValue: "yes",
        options: [
          { label: "Yes, current licence verified", value: "yes" },
          { label: "No, not yet verified", value: "no" }
        ]
      }
    ],
    outputs: [
      { id: "documentedInterestPaid", name: "Interest paid to document (NZ$)", type: "currency" },
      { id: "documentedFeesPaid", name: "Fees paid to document (NZ$)", type: "currency" },
      { id: "documentedChargesForReview", name: "Total charges to review, not a refund (NZ$)", type: "currency" },
      { id: "regulatorName", name: "Current CCCFA regulator", type: "text" },
      { id: "licensingRegime", name: "Licensing question to verify", type: "text" },
      { id: "enforcementPowers", name: "What the FMA and scheme can do", type: "text" },
      { id: "escalationRoute", name: "Complaint route", type: "text" },
      { id: "licensingAlert", name: "Licence verification note", type: "text" },
      { id: "issueNotice", name: "Evidence for this issue", type: "text" },
      { id: "contractDateNotice", name: "Loan date and current regulator", type: "text" },
      { id: "regulatoryNotice", name: "July 2026 transfer", type: "text" }
    ],
    formula: "calculateNzFmaCccfaEligibility"
  },

  {
    id: "altersvorsorgedepot-calculator-2027",
    name: "Germany Altersvorsorgedepot (Riester Replacement) Calculator (2027)",
    slug: "altersvorsorgedepot-calculator-2027",
    category: "retirement",
    description: "Calculate your state subsidies (Grundzulage & Kinderzulage) and projected ETF balance under Germany's 2027 Altersvorsorgedepot.",
    countries: ["de"],
    inputs: [
      { id: "annualContribution", name: "Annual Personal Contribution (€)", type: "currency", defaultValue: 1800, min: 0 },
      { id: "numberOfChildren", name: "Number of Qualifying Children", type: "number", defaultValue: 1, min: 0, max: 10 },
      {
        id: "isUnder25",
        name: "Saver Under Age 25 (Young Saver Bonus)?",
        type: "select",
        defaultValue: "no",
        options: [
          { label: "No — Age 25 or Older", value: "no" },
          { label: "Yes — Under Age 25 (€200 One-Off Bonus)", value: "yes" }
        ]
      },
      { id: "investmentTermYears", name: "Investment Duration to Retirement (Years)", type: "number", defaultValue: 30, min: 1, max: 50 },
      { id: "expectedEtfReturn", name: "Expected Annual ETF Portfolio Return (%)", type: "percent", defaultValue: 7.0, min: 0 }
    ],
    outputs: [
      { id: "annualContribution", name: "Annual Contribution (€)", type: "currency" },
      { id: "totalBaseSubsidy", name: "Annual State Base Subsidy (Grundzulage) (€)", type: "currency" },
      { id: "totalChildSubsidy", name: "Annual Child Subsidies (Kinderzulage) (€)", type: "currency" },
      { id: "youngBonus", name: "Young Saver One-Off Bonus (€)", type: "currency" },
      { id: "totalAnnualStateSubsidy", name: "Total Annual State Subsidies Earned (€)", type: "currency" },
      { id: "netPersonalOutOfPocket", name: "Personal Out-of-Pocket Contribution (€)", type: "currency" },
      { id: "totalAnnualDeposit", name: "Total Annual Account Deposit (€)", type: "currency" },
      { id: "totalPortfolioValue", name: "Projected ETF Portfolio Value at Retirement (€)", type: "currency" },
      { id: "totalPersonalInvested", name: "Total Personal Capital Out-of-Pocket (€)", type: "currency" },
      { id: "totalStateSubsidiesReceived", name: "Total Cumulative State Subsidies Received (€)", type: "currency" },
      { id: "totalCompoundInterestEarned", name: "Total Investment Growth & Dividends (€)", type: "currency" },
      { id: "effectiveSubsidyRate", name: "Effective State Subsidy Boost (%)", type: "percent" },
      { id: "reformNotice", name: "Altersvorsorgereformgesetz Legislation Notice", type: "text" }
    ],
    formula: "calculateAltersvorsorgedepotGrowth"
  },
  {
    id: "osfi-mortgage-stress-test-exemption-switch-calculator-2026",
    name: "Canada OSFI Mortgage Stress Test Switch Exemption Checker (2026)",
    slug: "osfi-mortgage-stress-test-exemption-switch-calculator-2026",
    category: "loans",
    description: "Check your eligibility for the OSFI mortgage stress test exemption on straight switch renewals and model qualifying rates vs contract rates.",
    countries: ["ca"],
    inputs: [
      { id: "mortgageBalance", name: "Current Outstanding Mortgage Balance ($)", type: "currency", defaultValue: 450000, min: 0 },
      { id: "propertyValue", name: "Current Property Value ($)", type: "currency", defaultValue: 650000, min: 0 },
      { id: "contractRate", name: "Offered New Lender Contract Rate (%)", type: "percent", defaultValue: 4.85, min: 0 },
      { id: "additionalCashOut", name: "Additional Cash-Out Refinance Amount ($)", type: "currency", defaultValue: 0, min: 0 },
      {
        id: "isAmortizationExtended",
        name: "Extending Amortization Period?",
        type: "select",
        defaultValue: "no",
        options: [
          { label: "No — Maintain Remaining Amortization (Straight Switch)", value: "no" },
          { label: "Yes — Extending Amortization Term", value: "yes" }
        ]
      },
      { id: "annualHouseholdIncome", name: "Annual Household Gross Income ($)", type: "currency", defaultValue: 110000, min: 0 }
    ],
    outputs: [
      { id: "totalMortgageAmount", name: "Total Mortgage Amount ($)", type: "currency" },
      { id: "contractRate", name: "Offered Contract Interest Rate (%)", type: "percent" },
      { id: "stressTestRate", name: "Standard OSFI Stress Test Rate (%)", type: "percent" },
      { id: "effectiveQualifyingRate", name: "Effective Qualifying Rate Required (%)", type: "percent" },
      { id: "actualMonthlyPayment", name: "Actual Monthly Payment at Contract Rate ($)", type: "currency" },
      { id: "stressTestMonthlyPayment", name: "Qualifying Monthly Stress Test Payment ($)", type: "currency" },
      { id: "ltvRatio", name: "Loan-to-Value (LTV) Ratio (%)", type: "percent" },
      { id: "ltiRatio", name: "Loan-to-Income (LTI) Multiple (x)", type: "number" },
      { id: "exemptionStatus", name: "OSFI Stress Test Exemption Status", type: "text" },
      { id: "exemptionDetails", name: "Regulatory Exemption & Policy Details", type: "text" },
      { id: "ltiAlert", name: "OSFI 4.5x LTI Portfolio Threshold Check", type: "text" }
    ],
    formula: "calculateOsfiMortgageSwitchExemption"
  },
  {
    id: "irs-afr-intra-family-loan-calculator-august-2026",
    name: "IRS August 2026 AFR Intra-Family Loan Calculator",
    slug: "irs-afr-intra-family-loan-calculator-august-2026",
    category: "investments",
    description: "Calculate statutory August 2026 IRS Applicable Federal Rates (AFR) for intra-family loans and check imputed interest gift tax risk under RR-2026-12.",
    countries: ["us"],
    inputs: [
      { id: "loanAmount", name: "Intra-Family Loan Principal ($)", type: "currency", defaultValue: 100000, min: 0 },
      { id: "loanTermYears", name: "Loan Term / Duration (Years)", type: "number", defaultValue: 5, min: 1, max: 30 },
      { id: "interestRateCharged", name: "Interest Rate Charged to Family Member (%)", type: "percent", defaultValue: 3.0, min: 0 },
      {
        id: "compoundingFrequency",
        name: "Compounding / Payment Frequency",
        type: "select",
        defaultValue: "annual",
        options: [
          { label: "Annual Compounding", value: "annual" },
          { label: "Monthly Compounding", value: "monthly" }
        ]
      }
    ],
    outputs: [
      { id: "termCategory", name: "IRS AFR Term Category", type: "text" },
      { id: "applicableAfrRate", name: "August 2026 Statutory IRS AFR Minimum (%)", type: "percent" },
      { id: "annualInterestCharged", name: "Actual Annual Interest Paid by Borrower ($)", type: "currency" },
      { id: "annualRequiredAfrInterest", name: "Statutory Required AFR Annual Interest ($)", type: "currency" },
      { id: "annualImputedInterest", name: "Annual Imputed Interest (Gift Deficit) ($)", type: "currency" },
      { id: "totalImputedInterestTerm", name: "Total Imputed Interest over Full Term ($)", type: "currency" },
      { id: "giftTaxRiskStatus", name: "IRS Gift Tax & Imputed Interest Status", type: "text" },
      { id: "taxNote", name: "IRC Section 7872 Imputed Interest Guidance", type: "text" },
      { id: "revRulingNotice", name: "IRS Revenue Ruling Notice", type: "text" }
    ],
    formula: "calculateIrsAfrIntraFamilyLoan"
  },
  {
    id: "fair-work-minimum-wage-increase-calculator-july-2026",
    name: "Australia Fair Work Minimum Wage & Award Increase Calculator (2026)",
    slug: "fair-work-minimum-wage-increase-calculator-july-2026",
    category: "tax",
    description: "Calculate your July 1, 2026 pay increase under Fair Work's 4.75% award increase and $26.44/hr National Minimum Wage with 25% casual loading.",
    countries: ["au"],
    inputs: [
      {
        id: "employmentType",
        name: "Award Tier & Employment Status",
        type: "select",
        defaultValue: "award_fulltime",
        options: [
          { label: "Modern Award — Full-Time / Part-Time (4.75% Increase)", value: "award_fulltime" },
          { label: "Modern Award — Casual (4.75% Increase + 25% Casual Loading)", value: "award_casual" },
          { label: "National Minimum Wage — Full-Time ($26.44/hr)", value: "national_minimum_fulltime" },
          { label: "National Minimum Wage — Casual ($33.05/hr with 25% Loading)", value: "national_minimum_casual" }
        ]
      },
      { id: "currentHourlyRate", name: "Current Base Hourly Pay Rate before July 1 (A$)", type: "currency", defaultValue: 30.00, min: 0 },
      { id: "weeklyHours", name: "Standard Weekly Hours Worked", type: "number", defaultValue: 38, min: 1, max: 70 }
    ],
    outputs: [
      { id: "newBaseHourlyRate", name: "New Base Hourly Rate (July 1, 2026) (A$)", type: "currency" },
      { id: "effectiveHourlyRate", name: "Effective Hourly Rate (incl. Casual Loading) (A$)", type: "currency" },
      { id: "hourlyIncreaseDollars", name: "Hourly Pay Increase Amount (A$)", type: "currency" },
      { id: "newWeeklyEarnings", name: "New Gross Weekly Earnings (A$)", type: "currency" },
      { id: "weeklyIncreaseDollars", name: "Weekly Pay Increase Amount (A$)", type: "currency" },
      { id: "annualIncreaseDollars", name: "Annual Gross Pay Increase (A$)", type: "currency" },
      { id: "casualLoadingPortion", name: "Weekly 25% Casual Loading Component (A$)", type: "currency" },
      { id: "increasePercent", name: "Fair Work Percentage Increase (%)", type: "percent" },
      { id: "categoryLabel", name: "Award / Minimum Wage Classification", type: "text" },
      { id: "fairWorkNotice", name: "Fair Work Commission Ruling Notice", type: "text" }
    ],
    formula: "calculateFairWorkMinimumWageIncrease"
  },
  {
    id: "cpf-retirement-age-transition-calculator-2026",
    name: "Singapore CPF Retirement Age Shift Simulator (2026)",
    slug: "cpf-retirement-age-transition-calculator-2026",
    category: "retirement",
    description: "Model Singapore's July 1, 2026 statutory retirement age shift (63→64 / 69 re-employment) and calculate your 4% SMRA compounding bonus.",
    countries: ["sg"],
    inputs: [
      { id: "currentAge", name: "Current Age in 2026", type: "number", defaultValue: 60, min: 50, max: 70 },
      { id: "raBalance", name: "Retirement Account (RA) Balance (S$)", type: "currency", defaultValue: 200000, min: 0 },
      { id: "oaBalance", name: "Ordinary Account (OA) Balance (S$)", type: "currency", defaultValue: 50000, min: 0 },
      { id: "targetPayoutAge", name: "Target CPF LIFE Payout Start Age", type: "number", defaultValue: 65, min: 65, max: 70 }
    ],
    outputs: [
      { id: "statutoryRetirementAge", name: "Statutory Retirement Age (July 1, 2026)", type: "number" },
      { id: "statutoryReemploymentAge", name: "Statutory Re-employment Age (July 1, 2026)", type: "number" },
      { id: "annualBaseInterestRA", name: "Annual 4.0% RA Base Compounding Interest (S$)", type: "currency" },
      { id: "extraInterestBonus", name: "Extra Interest Bonus (First $60k + Age 55+ Tier) (S$)", type: "currency" },
      { id: "totalAnnualInterestEarned", name: "Total 1-Year CPF Interest Earned (S$)", type: "currency" },
      { id: "projectedRaBalanceIn1Year", name: "Projected 1-Year Compounded RA Balance (S$)", type: "currency" },
      { id: "estimatedMonthlyPayoutBoostPercent", name: "Estimated CPF LIFE Monthly Payout Boost (%)", type: "percent" },
      { id: "statutoryStatus", name: "Statutory Policy Rule Status", type: "text" }
    ],
    formula: "calculateCpfRetirementAgeTransition"
  },
  {
    id: "stage-3-tax-cut-15-percent-bracket-calculator-2026",
    name: "Australia Stage 3 15% Tax Bracket & Asset Write-Off Calculator (2026-27)",
    slug: "stage-3-tax-cut-15-percent-bracket-calculator-2026",
    category: "tax",
    description: "Calculate your tax savings from Australia's July 1, 2026 15% lowest bracket drop and permanent $20,000 instant asset write-off.",
    countries: ["au"],
    inputs: [
      { id: "taxableSalary", name: "Annual Taxable Salary Income (A$)", type: "currency", defaultValue: 75000, min: 0 },
      { id: "soleTraderNetIncome", name: "Sole Trader / Net Business Income (A$)", type: "currency", defaultValue: 25000, min: 0 },
      { id: "eligibleAssetPurchases", name: "Eligible Asset Purchases under $20k Write-Off (A$)", type: "currency", defaultValue: 12000, min: 0 },
      { id: "businessUsePercent", name: "Business Use Percentage (%)", type: "percent", defaultValue: 100, min: 0, max: 100 }
    ],
    outputs: [
      { id: "totalGrossIncome", name: "Total Combined Gross Income (A$)", type: "currency" },
      { id: "cappedWriteOffDeduction", name: "$20,000 Instant Asset Write-Off Claimed (A$)", type: "currency" },
      { id: "netTaxableIncome", name: "Net Taxable Income after Deductions (A$)", type: "currency" },
      { id: "oldTotalTax", name: "Previous FY 2025-26 Tax Liability (A$)", type: "currency" },
      { id: "newTotalTax", name: "New FY 2026-27 Tax Liability (A$)", type: "currency" },
      { id: "taxCutSavings15Percent", name: "Tax Cut Savings from 15% Bracket (A$)", type: "currency" },
      { id: "writeOffTaxSavings", name: "Tax Savings from $20k Write-Off (A$)", type: "currency" },
      { id: "totalCombinedSavings", name: "Total Annual Tax Savings (A$)", type: "currency" },
      { id: "medicareLevy", name: "Medicare Levy (2%) (A$)", type: "currency" },
      { id: "netTakeHomeAnnual", name: "Net Annual Take-Home Pay (A$)", type: "currency" }
    ],
    formula: "calculateStage3TaxCut15PercentBracket"
  },
  {
    id: "rbi-mis-selling-compensation-calculator-2026",
    name: "RBI Anti-Mis-Selling Compensation & Refund Checker (2026)",
    slug: "rbi-mis-selling-compensation-calculator-2026",
    category: "loans",
    description: "Check potential refund & interest compensation claims for forced loan insurance and bancassurance mis-selling under RBI 2026 rules.",
    countries: ["in"],
    inputs: [
      { id: "loanAmount", name: "Baseline Principal Loan Amount (₹)", type: "currency", defaultValue: 2500000, min: 0 },
      { id: "forcedInsurancePremium", name: "Forced Bundled Insurance / ULIP Premium (₹)", type: "currency", defaultValue: 150000, min: 0 },
      { id: "hiddenProcessingFees", name: "Hidden Processing / Administrative Charges (₹)", type: "currency", defaultValue: 25000, min: 0 },
      { id: "monthsElapsed", name: "Months Elapsed Since Product Sale", type: "number", defaultValue: 12, min: 1, max: 120 },
      {
        id: "saleDatePeriod",
        name: "Product Sale Date",
        type: "select",
        defaultValue: "post_july_2026",
        options: [
          { label: "On/After July 1, 2026 (RB-IOS 2026 Strict Ombudsman Rules)", value: "post_july_2026" },
          { label: "Before July 1, 2026 (Standard Consumer Court Recourse)", value: "pre_july_2026" }
        ]
      }
    ],
    outputs: [
      { id: "baseMisSoldAmount", name: "Total Mis-Sold Principal Charges (₹)", type: "currency" },
      { id: "accruedInterestPenalty", name: "RBI Penalty Interest Accrued (9.0% p.a.) (₹)", type: "currency" },
      { id: "totalClaimAmount", name: "Total Estimated Compensation Claim (₹)", type: "currency" },
      { id: "forcedInsuranceRatio", name: "Forced Insurance Ratio to Loan Balance (%)", type: "percent" },
      { id: "liabilityNote", name: "RBI Framework & Strict Liability Status", type: "text" },
      { id: "ombudsmanCapStatus", name: "Statutory Claim Cap Status (₹30 Lakh Limit)", type: "text" }
    ],
    formula: "calculateRbiMisSellingCompensation"
  },
  {
    id: "student-loan-save-transition-calculator-2026",
    name: "Student Loan SAVE Transition & Payment Shock Calculator (2026)",
    slug: "student-loan-save-transition-calculator-2026",
    category: "loans",
    description: "Calculate your monthly payment shock, new 10% IDR / RAP payments, and PSLF eligibility following the March 2026 SAVE termination.",
    countries: ["us"],
    inputs: [
      { id: "agi", name: "Adjusted Gross Income (AGI $)", type: "currency", defaultValue: 65000, min: 0 },
      { id: "familySize", name: "Family Size", type: "number", defaultValue: 1, min: 1, max: 12 },
      { id: "loanBalance", name: "Total Federal Loan Balance ($)", type: "currency", defaultValue: 40000, min: 0 },
      { id: "interestRate", name: "Average Interest Rate (%)", type: "percent", defaultValue: 6.0, min: 0 },
      {
        id: "loanType",
        name: "Loan Type",
        type: "select",
        defaultValue: "direct_sub_unsub",
        options: [
          { label: "Direct Subsidized / Unsubsidized", value: "direct_sub_unsub" },
          { label: "Direct Grad PLUS", value: "direct_plus_grad" },
          { label: "Parent PLUS (Restricted)", value: "parent_plus" }
        ]
      },
      {
        id: "isPublicService",
        name: "Qualifying Public Service / Non-Profit Employment (PSLF)?",
        type: "select",
        defaultValue: "no",
        options: [
          { label: "No — Private / Commercial Sector", value: "no" },
          { label: "Yes — Government / 501(c)(3) Non-Profit", value: "yes" }
        ]
      }
    ],
    outputs: [
      { id: "oldSaveMonthlyPayment", name: "Old Terminated SAVE Monthly Payment (~5%)", type: "currency" },
      { id: "newIdrMonthlyPayment", name: "New 10% IDR Monthly Payment", type: "currency" },
      { id: "rapMonthlyPayment", name: "Repayment Assistance Plan (RAP) Payment", type: "currency" },
      { id: "rapEligibilityStatus", name: "RAP Plan Eligibility Status", type: "text" },
      { id: "standard10YrMonthlyPayment", name: "Standard 10-Year Repayment Payment", type: "currency" },
      { id: "paymentShockDollar", name: "Monthly Payment Increase (Payment Shock)", type: "currency" },
      { id: "paymentShockPercent", name: "Payment Shock Percentage Increase", type: "percent" },
      { id: "pslfStatus", name: "Public Service Loan Forgiveness (PSLF) Status", type: "text" },
      { id: "recommendedPlan", name: "Recommended Repayment Plan", type: "text" }
    ],
    formula: "calculateStudentLoanSaveTransition"
  },
  {
    id: "hra-exemption-calculator-8-cities",
    name: "8-City HRA Exemption Calculator (FY 2026-27)",
    slug: "hra-exemption-calculator-8-cities",
    category: "tax",
    description: "Calculate your HRA tax exemption under the 8-city expanded rule (adding Bengaluru, Pune, Hyderabad, Ahmedabad) for FY 2026-27.",
    countries: ["in"],
    inputs: [
      { id: "basicSalaryAnnual", name: "Annual Basic Salary (₹)", type: "currency", defaultValue: 600000, min: 0 },
      { id: "daAnnual", name: "Annual Dearness Allowance DA (₹)", type: "currency", defaultValue: 0, min: 0 },
      { id: "hraReceivedAnnual", name: "Annual HRA Allowance Received (₹)", type: "currency", defaultValue: 240000, min: 0 },
      { id: "rentPaidAnnual", name: "Annual Total Rent Paid (₹)", type: "currency", defaultValue: 300000, min: 0 },
      {
        id: "cityCategory",
        name: "City Category (8-City Metro List)",
        type: "select",
        defaultValue: "metro8",
        options: [
          { label: "50% Exemption: 8 Metros (Mumbai, Delhi, Kolkata, Chennai, Bengaluru, Pune, Hyderabad, Ahmedabad)", value: "metro8" },
          { label: "40% Exemption: Non-Metro / All Other Cities", value: "nonmetro" }
        ]
      },
      {
        id: "taxRegime",
        name: "Tax Regime Selected",
        type: "select",
        defaultValue: "old",
        options: [
          { label: "Old Tax Regime (HRA Allowed)", value: "old" },
          { label: "New Tax Regime (HRA Not Allowed)", value: "new" }
        ]
      }
    ],
    outputs: [
      { id: "salaryBase", name: "Salary Base (Basic + DA)", type: "currency" },
      { id: "exemptHra", name: "Exempt HRA Amount (Tax-Free)", type: "currency" },
      { id: "taxableHra", name: "Taxable HRA Portion", type: "currency" },
      { id: "taxSaved", name: "Income Tax Saved", type: "currency" },
      { id: "panRequiredNote", name: "Landlord PAN Disclosure Requirement", type: "text" },
      { id: "regimeNote", name: "City & Exemption Category Note", type: "text" },
      { id: "ruleStatus", name: "Legislative Rule Status", type: "text" }
    ],
    formula: "calculateHraExemption8Cities"
  },
  {
    id: "401k-contribution-calculator-2026",
    name: "401(k) Contribution Limits Maximizer (2026)",
    slug: "401k-contribution-calculator-2026",
    category: "retirement",
    description: "Calculate per-paycheck 401(k) deferrals to hit 2026 limits ($24,500 base, $11,250 Super Catch-Up for ages 60-63) and claim full employer match.",
    countries: ["us"],
    inputs: [
      { id: "annualSalary", name: "Gross Annual Salary ($)", type: "currency", defaultValue: 120000, min: 0 },
      { id: "age", name: "Your Age in 2026", type: "number", defaultValue: 45, min: 18, max: 85 },
      {
        id: "paycheckFrequency",
        name: "Paycheck Frequency",
        type: "select",
        defaultValue: "biweekly",
        options: [
          { label: "Bi-Weekly (26 paychecks/yr)", value: "biweekly" },
          { label: "Semi-Monthly (24 paychecks/yr)", value: "semi_monthly" },
          { label: "Monthly (12 paychecks/yr)", value: "monthly" },
          { label: "Weekly (52 paychecks/yr)", value: "weekly" }
        ]
      },
      { id: "ytdContributions", name: "2026 YTD Employee Contributions ($)", type: "currency", defaultValue: 0, min: 0 },
      { id: "remainingPaychecks", name: "Remaining Paychecks in 2026", type: "number", defaultValue: 12, min: 1, max: 52 },
      { id: "employerMatchPercent", name: "Employer Match % (e.g. 50% match)", type: "percent", defaultValue: 50, min: 0, max: 100 },
      { id: "employerMatchCapPercent", name: "Employer Match Salary Cap % (e.g. up to 6%)", type: "percent", defaultValue: 6, min: 0, max: 100 },
      { id: "priorYearW2Income", name: "Prior Year W-2 Income ($)", type: "currency", defaultValue: 120000, min: 0 }
    ],
    outputs: [
      { id: "totalEmployeeLimit", name: "2026 Maximum Employee Deferral Limit", type: "currency" },
      { id: "remainingEmployeeLimit", name: "Remaining Employee Contribution Needed", type: "currency" },
      { id: "perPaycheckAmount", name: "Required Contribution per Paycheck ($)", type: "currency" },
      { id: "perPaycheckPercent", name: "Required Deferral Rate (% of Paycheck)", type: "percent" },
      { id: "employerMatchDollars", name: "Estimated Employer Match Earned ($)", type: "currency" },
      { id: "totalCombinedContributions", name: "Total Combined 401(k) Addition ($)", type: "currency" },
      { id: "estimatedTaxSavings", name: "Estimated Federal Income Tax Savings ($)", type: "currency" },
      { id: "tierName", name: "2026 Contribution Tier & Rule", type: "text" },
      { id: "rothMandateAlert", name: "SECURE 2.0 Roth Catch-Up Mandate Status", type: "text" }
    ],
    formula: "calculate401kContributionMaximizer"
  },
  {
    id: "13th-ahv-pension-calculator-2026",
    name: "13th AHV Pension Payout Estimator (Switzerland)",
    slug: "13th-ahv-pension-calculator-2026",
    category: "retirement",
    description: "Estimate your December 2026 13th AHV/OASI pension windfall payout, single vs. couple caps, and Scale 44 contribution rate.",
    countries: ["ch"],
    inputs: [
      { 
        id: "maritalStatus", 
        name: "Marital Status", 
        type: "select", 
        defaultValue: "single", 
        options: [
          { label: "Single / Unmarried", value: "single" },
          { label: "Married Couple", value: "married" }
        ] 
      },
      { id: "contributionYears", name: "Contribution Years (Scale 44: Max 44)", type: "number", defaultValue: 44, min: 1, max: 44 },
      { id: "monthlyAhvPension", name: "Regular Monthly AHV Pension (CHF)", type: "currency", defaultValue: 2520, min: 0, max: 3780 }
    ],
    outputs: [
      { id: "monthlyAhvPension", name: "Effective Monthly AHV Pension", type: "currency" },
      { id: "regularAnnualPension", name: "Regular 12-Month AHV Payout", type: "currency" },
      { id: "december13thBonus", name: "December 13th AHV Payout Windfall", type: "currency" },
      { id: "totalAnnualAHV", name: "Total 13-Month Annual AHV Income", type: "currency" },
      { id: "scalePercentage", name: "Contribution Scale Ratio", type: "percent" },
      { id: "coupleCapStatus", name: "Plafonierung / Married Cap Status", type: "text" }
    ],
    formula: "calculate13thAhvPension"
  },
  {
    id: "irs-mileage-rate-calculator-2026",
    name: "IRS Mid-Year Standard Mileage Rate Split Calculator (2026)",
    slug: "irs-mileage-rate-calculator-2026",
    category: "tax",
    description: "Calculate your split-year 2026 vehicle tax deduction (H1 at 72.5¢ vs H2 at 76¢ per mile) under IRS Announcement 2026-11.",
    countries: ["us"],
    inputs: [
      { id: "businessMilesH1", name: "Business Miles Driven (Jan 1 – June 30, 2026)", type: "currency", defaultValue: 5000, min: 0 },
      { id: "businessMilesH2", name: "Business Miles Driven (July 1 – Dec 31, 2026)", type: "currency", defaultValue: 5000, min: 0 },
      { id: "medicalMilesH1", name: "Medical/Moving Miles Driven (Jan 1 – June 30, 2026)", type: "currency", defaultValue: 0, min: 0 },
      { id: "medicalMilesH2", name: "Medical/Moving Miles Driven (July 1 – Dec 31, 2026)", type: "currency", defaultValue: 0, min: 0 },
      { id: "charityMiles", name: "Charitable Miles Driven (All 2026)", type: "currency", defaultValue: 0, min: 0 },
      { id: "marginalTaxRate", name: "Marginal Tax Rate (%)", type: "percent", defaultValue: 24, min: 0, max: 50 },
      {
        id: "usedActualExpensesH1",
        name: "Claimed Actual Expenses in H1 for Vehicle?",
        type: "select",
        defaultValue: "no",
        options: [
          { label: "No — Used Standard Mileage Rate in H1", value: "no" },
          { label: "Yes — Claimed Actual Vehicle Expenses in H1", value: "yes" }
        ]
      }
    ],
    outputs: [
      { id: "h1BusinessDeduction", name: "H1 Business Mileage Deduction (72.5¢)", type: "currency" },
      { id: "h2BusinessDeduction", name: "H2 Business Mileage Deduction (76.0¢)", type: "currency" },
      { id: "totalBusinessDeduction", name: "Total Business Mileage Deduction", type: "currency" },
      { id: "medicalMovingDeduction", name: "Total Medical & Moving Deduction", type: "currency" },
      { id: "charityDeduction", name: "Total Charitable Mileage Deduction", type: "currency" },
      { id: "totalMileageDeduction", name: "Total Combined Mileage Deduction", type: "currency" },
      { id: "estimatedTaxSavings", name: "Estimated Income Tax Savings", type: "currency" },
      { id: "blendedBusinessRate", name: "Blended Rate per Business Mile (cents)", type: "number" },
      { id: "disallowedWarning", name: "IRS Mileage Eligibility & Compliance Status", type: "text" }
    ],
    formula: "calculateIrsMileageRateSplit"
  },
  {
    id: "no-tax-on-tips",
    name: "No Tax on Tips Savings Calculator",
    slug: "no-tax-on-tips",
    category: "tax",
    description: "Calculate your tax savings from the OBBBA 'No Tax on Tips' federal deduction.",
    countries: ["us"],
    inputs: [
      { id: "tipIncome", name: "Annual Tip Income", type: "currency", defaultValue: 10000, min: 0 },
      filingStatusInput,
      magiInput
    ],
    outputs: [
      { id: "deduction", name: "Qualified Tip Deduction", type: "currency" },
      { id: "taxSaved", name: "Federal Income Tax Saved", type: "currency" }
    ],
    formula: "calculateNoTaxOnTips"
  },
  {
    id: "no-tax-on-overtime",
    name: "No Tax on Overtime Calculator",
    slug: "no-tax-on-overtime",
    category: "tax",
    description: "Estimate your federal income tax savings under the OBBBA overtime deduction.",
    countries: ["us"],
    inputs: [
      { id: "overtimeIncome", name: "Annual Overtime Income", type: "currency", defaultValue: 5000, min: 0 },
      filingStatusInput,
      magiInput
    ],
    outputs: [
      { id: "deduction", name: "Qualified Overtime Deduction", type: "currency" },
      { id: "taxSaved", name: "Federal Income Tax Saved", type: "currency" }
    ],
    formula: "calculateNoTaxOnOvertime"
  },
  {
    id: "senior-deduction",
    name: "Senior Deduction Calculator",
    slug: "senior-deduction",
    category: "tax",
    description: "Calculate your additional $6,000 above-the-line senior deduction under the OBBBA.",
    countries: ["us"],
    inputs: [
      { id: "age", name: "Your Age", type: "number", defaultValue: 65, min: 0 },
      filingStatusInput,
      magiInput
    ],
    outputs: [
      { id: "deduction", name: "Additional Senior Deduction", type: "currency" },
      { id: "taxSaved", name: "Federal Income Tax Saved", type: "currency" }
    ],
    formula: "calculateSeniorDeduction"
  },
  {
    id: "instant-deduction",
    name: "Instant Tax Deduction Calculator",
    slug: "instant-deduction",
    category: "tax",
    description: "Compare your tax savings from the new $1,000 standard deduction against your actual (itemized) work-related expenses and the old $300 no-receipt limit, for the 2026-27 income year.",
    countries: ["au"],
    inputs: [
      { id: "grossIncome", name: "Annual Taxable Income (AUD)", type: "currency", defaultValue: 85000, min: 0 },
      { id: "actualExpenses", name: "Actual Work-Related Expenses (AUD)", type: "currency", defaultValue: 300, min: 0 },
      { 
        id: "hasReceipts", 
        name: "Do you have valid receipts/records for all expenses?", 
        type: "select", 
        options: [
          { label: "No (capped under no-receipt rules)", value: "no" },
          { label: "Yes (can claim full actual amount)", value: "yes" }
        ], 
        defaultValue: "no" 
      }
    ],
    outputs: [
      { id: "standardDeduction", name: "New Standard Work Deduction ($1,000)", type: "currency" },
      { id: "itemizedDeduction", name: "Itemized Actual Expense Claim", type: "currency" },
      { id: "recommendedClaim", name: "Recommended Total Claim", type: "currency" },
      { id: "oldClaim", name: "Deduction Claim under Old Rules", type: "currency" },
      { id: "newClaim", name: "Deduction Claim under New Rules", type: "currency" },
      { id: "deductionIncrease", name: "Net Increase in Deductions", type: "currency" },
      { id: "marginalRate", name: "Your Marginal Tax Rate", type: "percent" },
      { id: "medicareLevy", name: "Medicare Levy Rate", type: "percent" },
      { id: "effectiveRate", name: "Combined Tax Benefit Rate", type: "percent" },
      { id: "oldTaxSavings", name: "Estimated Old Tax Savings", type: "currency" },
      { id: "newTaxSavings", name: "Estimated New Tax Savings", type: "currency" },
      { id: "netTaxSaved", name: "Additional Annual Tax Saved", type: "currency" },
      { id: "homeOfficeOverlap", name: "Home Office & Record Keeping Rule", type: "text" }
    ],
    formula: "calculateInstantTaxDeduction"
  },
  {
    id: "auto-loan-interest-deduction",
    name: "Auto Loan Interest Deduction Calculator",
    slug: "auto-loan-interest-deduction",
    category: "tax",
    description: "Estimate your deductible auto loan interest for new, US-made passenger vehicles.",
    countries: ["us"],
    inputs: [
      { id: "autoInterestPaid", name: "Annual Auto Interest Paid", type: "currency", defaultValue: 1200, min: 0 },
      { id: "magi", name: "Modified AGI (MAGI)", type: "currency", defaultValue: 50000, min: 0 },
      { id: "autoUsAssembled", name: "Assembled in United States", type: "select", defaultValue: "yes", options: [{ label: "Yes", value: "yes" }, { label: "No", value: "no" }] }
    ],
    outputs: [
      { id: "deduction", name: "Deductible Auto Loan Interest", type: "currency" },
      { id: "taxSaved", name: "Federal Income Tax Saved", type: "currency" }
    ],
    formula: "calculateAutoLoanInterestDeduction"
  },
  {
    id: "obbba-total-savings",
    name: "OBBBA Total Savings Calculator",
    slug: "obbba-total-savings",
    category: "tax",
    description: "Model your combined federal income tax savings across all four new OBBBA deductions.",
    countries: ["us"],
    inputs: [
      { id: "filingStatus", name: "Filing Status", type: "select", defaultValue: "single", options: [{ label: "Single", value: "single" }, { label: "Married Filing Jointly", value: "married_jointly" }, { label: "Head of Household", value: "head_of_household" }] },
      { id: "magi", name: "Modified AGI (MAGI)", type: "currency", defaultValue: 60000, min: 0 },
      { id: "tipIncome", name: "Annual Tip Income", type: "currency", defaultValue: 12000, min: 0 },
      { id: "overtimeIncome", name: "Annual Overtime Income", type: "currency", defaultValue: 15000, min: 0 },
      { id: "age", name: "Your Age", type: "number", defaultValue: 35, min: 0 },
      { id: "autoInterestPaid", name: "Annual Auto Interest Paid", type: "currency", defaultValue: 4000, min: 0 },
      { id: "autoUsAssembled", name: "Vehicle Assembled in US", type: "select", defaultValue: "yes", options: [{ label: "Yes", value: "yes" }, { label: "No", value: "no" }] }
    ],
    outputs: [
      { id: "tipsDeduction", name: "Tips Deduction", type: "currency" },
      { id: "overtimeDeduction", name: "Overtime Deduction", type: "currency" },
      { id: "seniorDeduction", name: "Senior Deduction", type: "currency" },
      { id: "autoInterestDeduction", name: "Auto Interest Deduction", type: "currency" },
      { id: "totalDeduction", name: "Total OBBBA Deduction", type: "currency" },
      { id: "taxSaved", name: "Total Federal Tax Saved", type: "currency" }
    ],
    formula: "calculateObbbaTotalSavings"
  },

  // --- LOAN CALCULATORS ---
  {
    id: "apra-dti-cap-check",
    name: "APRA DTI Cap Check Calculator",
    slug: "apra-dti-cap-check",
    category: "loans",
    description: "Check whether your new home loan falls within APRA's 20% high-DTI lending cap based on your income and existing debts.",
    countries: ["au"],
    inputs: [
      { id: "annualIncome", name: "Gross Annual Income (AUD)", type: "currency", defaultValue: 100000, min: 0 },
      { id: "currentTotalDebt", name: "Current Total Debt (AUD)", type: "currency", defaultValue: 400000, min: 0 },
      { id: "newLoanAmount", name: "New Loan Amount (AUD)", type: "currency", defaultValue: 200000, min: 0 },
      { id: "existingMonthlyDebtPayments", name: "Existing Monthly Debt Payments (AUD)", type: "currency", defaultValue: 2200, min: 0 }
    ],
    outputs: [
      { id: "dtiMultiplier", name: "Debt-to-Income Ratio (x)", type: "number" },
      { id: "exceedsThreshold", name: "Exceeds 6x DTI Threshold?", type: "text" },
      { id: "totalDebtAfterLoan", name: "Total Debt After New Loan", type: "currency" },
      { id: "monthlyDebtPaymentsAfterLoan", name: "Estimated Total Monthly Debt Payments", type: "currency" },
      { id: "estimatedMaxBorrowing", name: "Estimated Max Total Debt at 6x", type: "currency" }
    ],
    formula: "calculateApraDtiCapCheck"
  },
  {
    id: "offset-error-cost-calculator",
    name: "Offset Account Error Cost Calculator",
    slug: "offset-error-cost-calculator",
    category: "loans",
    description: "Work out how much extra home-loan interest you paid while your offset account sat unlinked, the failure ASIC's REP 837 found at 8 major banks.",
    countries: ["au"],
    inputs: [
      { id: "loanBalance", name: "Home Loan Balance (AUD)", type: "currency", defaultValue: 600000, min: 0, max: 5000000 },
      { id: "offsetBalance", name: "Offset Account Balance (AUD)", type: "currency", defaultValue: 100000, min: 0, max: 5000000 },
      { id: "interestRate", name: "Home Loan Interest Rate (%)", type: "percent", defaultValue: 5.9, min: 0.1, max: 15, step: 0.05 },
      { id: "monthsUnlinked", name: "Months the Offset Was Not Working", type: "number", defaultValue: 12, min: 1, max: 72 }
    ],
    outputs: [
      { id: "extraInterestPaid", name: "Estimated Extra Interest Paid", type: "currency" },
      { id: "monthlyExtraInterest", name: "Extra Interest per Month", type: "currency" },
      { id: "dailyExtraInterest", name: "Extra Interest per Day", type: "currency" },
      { id: "effectiveOffsetBalance", name: "Offset Balance Counted (Capped at Loan)", type: "currency" }
    ],
    formula: "calculateOffsetErrorCost"
  },

  // --- INVESTMENT CALCULATORS ---

  // --- RETIREMENT CALCULATORS ---

  // --- SAVINGS & BUDGETING CALCULATORS ---
  {
    id: "ssb-calculator",
    name: "Singapore Savings Bonds (SSB) Calculator",
    slug: "ssb-calculator",
    category: "savings",
    description: "Calculate whole-year coupon receipts for the August 2026 Singapore Savings Bond GX26080T, with its published step-up ladder and S$500 blocks.",
    countries: ["sg"],
    inputs: [
      { id: "principal", name: "Investment Principal (SGD)", type: "currency", defaultValue: 10000, min: 500, max: 200000, step: 500 },
      { id: "yearsToHold", name: "Holding Period (Years)", type: "number", defaultValue: 10, min: 1, max: 10 }
    ],
    outputs: [
      { id: "totalInterest", name: "Total Interest Earned", type: "currency" },
      { id: "averageYield", name: "Simple average annual coupon rate", type: "percent" },
      { id: "modeledPrincipal", name: "S$500-block principal modelled", type: "currency" },
      { id: "endBalance", name: "Principal plus lifetime coupons received", type: "currency" },
      { id: "denominationNotice", name: "Bond denomination and holding limit", type: "text" }
    ],
    formula: "calculateSingaporeSavingsBond"
  },
  {
    id: "capital-one-360-settlement-payout-estimator",
    name: "Capital One 360 Settlement Payout Estimator",
    slug: "capital-one-360-settlement-payout-estimator",
    category: "savings",
    description: "Model the interest your 360 Savings account missed out on and an illustrative pro-rata share of the $425 million settlement fund now delayed by an appeal.",
    countries: ["us"],
    inputs: [
      { id: "avgBalance", name: "Average 360 Savings Balance ($)", type: "currency", defaultValue: 10000, min: 0, max: 5000000 },
      { id: "monthsHeld", name: "Months Held Within Sep 2019 to Jun 2025", type: "number", defaultValue: 69, min: 1, max: 69 },
      { id: "avgRateGap", name: "Average APY Gap vs 360 Performance (%)", type: "percent", defaultValue: 1.9, min: 0.1, max: 4.05, step: 0.05 },
      { id: "stillHasAccount", name: "Do You Still Hold the 360 Savings Account?", type: "select", defaultValue: "yes", options: [
        { label: "Yes, still open", value: "yes" },
        { label: "No, closed", value: "no" }
      ]}
    ],
    outputs: [
      { id: "recognizedClaimEstimate", name: "Estimated Foregone Interest (Recognized Claim)", type: "currency" },
      { id: "estimatedPayoutLow", name: "Illustrative Payout, Conservative Scenario", type: "currency" },
      { id: "estimatedPayoutHigh", name: "Illustrative Payout, Upper Scenario", type: "currency" },
      { id: "annualFutureBenefit", name: "Extra Interest per Year From Rate Matching", type: "currency" }
    ],
    formula: "calculateCapitalOne360SettlementEstimate"
  },
  {
    id: "inflation-impact",
    name: "Inflation Impact Calculator",
    slug: "inflation-impact",
    category: "savings",
    description: "Examine how rising prices erode the future purchasing power of your cash.",
    countries: ["us"],
    inputs: [
      { id: "currentAmount", name: "Current Cash Amount", type: "currency", defaultValue: 10000, min: 0 },
      { id: "annualInflation", name: "Expected Annual Inflation %", type: "percent", defaultValue: 3.0, min: 0 },
      { id: "termYears", name: "Time Period (Years)", type: "number", defaultValue: 10, min: 1 }
    ],
    outputs: [
      { id: "futurePurchasingPower", name: "Future Purchasing Power Value", type: "currency" },
      { id: "lossInValue", name: "Loss in Cash Value / Power", type: "currency" }
    ],
    formula: "inflationImpact"
  },

  
  // --- COUNTRY SPECIFIC CALCULATORS ---
  {
    id: "trump-account-growth",
    name: "Trump Account Savings & Growth Calculator",
    slug: "trump-account-growth",
    category: "savings",
    description: "Calculate Section 530A Trump Account savings, government seed compounding, and taxable basis splits under OBBBA.",
    countries: ["us"],
    inputs: [
      { id: "childBirthYear", name: "Child Birth Year", type: "select", defaultValue: 2026, options: [
        { label: "2025", value: 2025 },
        { label: "2026", value: 2026 },
        { label: "2027", value: 2027 },
        { label: "2028", value: 2028 },
        { label: "2029 or later (no $1,000 federal seed)", value: 2029 }
      ]},
      { id: "includeFederalSeed", name: "Include $1,000 Federal Seed", type: "select", defaultValue: "yes", options: [
        { label: "Yes, Elect $1,000 Seed", value: "yes" },
        { label: "No Federal Seed", value: "no" }
      ]},
      { id: "currentAge", name: "Child Current Age", type: "number", defaultValue: 0, min: 0, max: 17 },
      { id: "employerContribution", name: "Annual Employer Contribution ($)", type: "currency", defaultValue: 0, min: 0, max: 2500 },
      { id: "annualContribution", name: "Annual Individual Contribution ($)", type: "currency", defaultValue: 2000, min: 0, max: 5000 },
      { id: "annualReturn", name: "Expected Annual Return (%)", type: "percent", defaultValue: 7.5, min: 1, max: 15 },
      { id: "retirementAge", name: "Target Retirement Age", type: "number", defaultValue: 65, min: 18, max: 80 }
    ],
    outputs: [
      { id: "endBalance", name: "Projected Balance at Retirement", type: "currency" },
      { id: "totalContributions", name: "Total Combined Contributions", type: "currency" },
      { id: "totalBasis", name: "Tax-Free Basis (Individual)", type: "currency" },
      { id: "totalTaxable", name: "Taxable Portion", type: "currency" },
      { id: "taxFreePercent", name: "Tax-Free Basis (%)", type: "percent" },
      { id: "taxablePercent", name: "Taxable Portion (%)", type: "percent" },
      { id: "balanceAt18", name: "Projected Balance at Age 18", type: "currency" },
      { id: "balanceAt27", name: "Projected Balance at Age 27", type: "currency" },
      { id: "balanceAt55", name: "Projected Balance at Age 55", type: "currency" },
      { id: "balanceAt65", name: "Projected Balance at Age 65", type: "currency" }
    ],
    formula: "calculateTrumpAccountGrowth"
  },
  {
    id: "cash-isa-2027-cap-impact",
    name: "Cash ISA 2027 Cap Impact Calculator",
    slug: "cash-isa-2027-cap-impact",
    category: "investments",
    description: "Estimate the effect of the enacted £12,000 annual Cash ISA cap from April 2027 for savers aged 64 or under at tax-year end, against a £20,000 cash-ISA counterfactual.",
    countries: ["gb"],
    inputs: [
      { id: "currentSavings", name: "Balance Already in Cash ISAs (£)", type: "currency", defaultValue: 10000, min: 0 },
      { id: "monthlyContribution", name: "Monthly Contribution (£)", type: "currency", defaultValue: 1666.67, min: 0 },
      { id: "interestRate", name: "Expected Interest Rate (%)", type: "percent", defaultValue: 4.5, min: 0 },
      { id: "age", name: "Age on 5 April 2028", type: "number", defaultValue: 35, min: 18 },
      { id: "taxRate", name: "2027–28 Savings Tax Rate", type: "select", defaultValue: 22, options: [{ label: "Basic Rate (22%)", value: 22 }, { label: "Higher Rate (42%)", value: 42 }, { label: "Additional Rate (47%)", value: 47 }] }
    ],
    outputs: [
      { id: "balanceOldRules10y", name: "10-Year Balance (Old)", type: "currency" },
      { id: "balanceNewRules10y", name: "10-Year Balance (New)", type: "currency" },
      { id: "compoundingCost10y", name: "10-Year Wealth Loss", type: "currency" },
      { id: "balanceOldRules30y", name: "30-Year Balance (Old)", type: "currency" },
      { id: "balanceNewRules30y", name: "30-Year Balance (New)", type: "currency" },
      { id: "compoundingCost30y", name: "30-Year Wealth Loss", type: "currency" }
    ],
    formula: "calculateCashIsa2027CapImpact"
  },
  {
    id: "payday-super-impact",
    name: "Payday Super Impact Calculator",
    slug: "payday-super-impact",
    category: "retirement",
    description: "Calculate the compounding interest savings of weekly or fortnightly super contributions versus quarterly payments.",
    countries: ["au"],
    inputs: [
      { id: "salary", name: "Annual Gross Salary", type: "currency", defaultValue: 90000, min: 0 },
      { id: "currentBalance", name: "Current Super Balance", type: "currency", defaultValue: 50000, min: 0 },
      { id: "years", name: "Years to Retirement", type: "number", defaultValue: 25, min: 1 },
      { id: "returnRate", name: "Expected Annual Return", type: "percent", defaultValue: 7.5, min: 1, max: 15 },
      { id: "frequency", name: "Payment Frequency", type: "select", defaultValue: "fortnightly", options: [{ label: "Weekly", value: "weekly" }, { label: "Fortnightly", value: "fortnightly" }, { label: "Monthly", value: "monthly" }] }
    ],
    outputs: [
      { id: "quarterlyFinal", name: "Projected Quarterly Super Balance", type: "currency" },
      { id: "paydayFinal", name: "Projected Payday Super Balance", type: "currency" },
      { id: "extraSavings", name: "Additional Compounding Savings", type: "currency" }
    ],
    formula: "calculatePaydaySuperImpact"
  },
  {
    id: "payday-super-compliance",
    name: "Payday Super Compliance Calculator",
    slug: "payday-super-compliance",
    category: "retirement",
    description: "Estimate the business cash flow and working capital interest impact of moving from quarterly to per-cycle Payday Super contributions.",
    countries: ["au"],
    inputs: [
      { id: "employees", name: "Number of Employees", type: "number", defaultValue: 10, min: 1 },
      { id: "avgSalary", name: "Average Employee Annual Salary", type: "currency", defaultValue: 85000, min: 0 },
      { id: "payCycle", name: "Employee Pay Cycle", type: "select", defaultValue: "fortnightly", options: [{ label: "Weekly", value: "weekly" }, { label: "Fortnightly", value: "fortnightly" }, { label: "Monthly", value: "monthly" }] }
    ],
    outputs: [
      { id: "quarterlyOutflow", name: "Remittance per Quarter (Old)", type: "currency" },
      { id: "cycleOutflow", name: "Remittance per Pay Cycle (New)", type: "currency" },
      { id: "workingCapitalInterestLost", name: "Annual Working Capital Interest Cost", type: "currency" }
    ],
    formula: "calculatePaydaySuperCompliance"
  },
  {
    id: "budget-2026-cpf-top-up-checker",
    name: "Budget 2026 CPF Top-Up Checker",
    slug: "budget-2026-cpf-top-up-checker",
    category: "retirement",
    description: "Check if you qualify for the one-off Budget 2026 CPF top-up of up to $1,500 for Singaporeans aged 50+ with lower retirement savings. Means-tested by CPF savings and property annual value.",
    countries: ["sg"],
    inputs: [
      { id: "birthYear", name: "Year of Birth", type: "number", defaultValue: 1970, min: 1900, max: 2026, tooltip: "You must be born in 1976 or earlier (aged 50+ in 2026) to qualify" },
      { id: "cpfRetirementSavings", name: "CPF Retirement Savings as at 31 Dec 2025", type: "currency", defaultValue: 50000, min: 0, tooltip: "Sum of RA and CPF LIFE balances, or OA + SA balances if RA not yet created" },
      { id: "propertyAnnualValue", name: "Property Annual Value (AV)", type: "currency", defaultValue: 15000, min: 0, tooltip: "Annual Value of your residential property as shown in your IRAS tax bill" },
      { id: "numberOfProperties", name: "Number of Properties Owned", type: "number", defaultValue: 1, min: 0, tooltip: "Must own not more than 1 property to qualify" }
    ],
    outputs: [
      { id: "topUpAmount", name: "CPF Top-Up Amount", type: "currency" },
        { id: "isEligible", name: "Eligible?", type: "text" },
      { id: "ageIn2026", name: "Your Age in 2026", type: "number" },
      { id: "brs2026", name: "2026 Basic Retirement Sum", type: "currency" }
    ],
    formula: "calculateBudget2026CpfTopUp"
  },
  {
    id: "pillar-3a-retroactive-buy-in",
    name: "Pillar 3a Retroactive Buy-In Calculator",
    slug: "pillar-3a-retroactive-buy-in",
    category: "retirement",
    description: "Calculate your tax savings and pension growth under Switzerland's new 2026 rule allowing retroactive buy-ins for missed Pillar 3a contributions.",
    countries: ["ch"],
    inputs: [
      { id: "employmentType", name: "Employment Type", type: "select", defaultValue: "salaried", options: [{ label: "Salaried (with Pension Fund / Pillar 2)", value: "salaried" }, { label: "Self-employed (without Pension Fund)", value: "self_employed" }] },
      { id: "taxableIncome", name: "Annual Taxable Income (CHF)", type: "number", defaultValue: 100000, min: 10000, max: 1000000, step: 5000 },
      { id: "gapYears", name: "Number of Gap Years to Close", type: "number", defaultValue: 3, min: 1, max: 10, step: 1 },
      { id: "marginalTaxRate", name: "Combined Marginal Tax Rate (%)", type: "percent", defaultValue: 25.0, min: 5, max: 55, step: 0.5 },
      { id: "expectedReturn", name: "Expected Annual Investment Return (%)", type: "percent", defaultValue: 6.0, min: 0, max: 15, step: 0.1 }
    ],
    outputs: [
      { id: "gapYears", name: "Missed Years to Close", type: "number" },
      { id: "annualLimit", name: "Max Allowed Buy-In per Year", type: "currency" },
      { id: "totalGapVal", name: "Total Gap Value to Close", type: "currency" },
      { id: "annualTaxSavings", name: "Estimated Annual Tax Savings", type: "currency" },
      { id: "totalTaxSavings", name: "Total Estimated Tax Savings", type: "currency" },
      { id: "timeToClose", name: "Time Required to Complete Buy-Ins (Years)", type: "number" },
      { id: "pensionGrowth10Yr", name: "Compounded Catch-up Pension (10 Years)", type: "currency" },
      { id: "pensionGrowth20Yr", name: "Compounded Catch-up Pension (20 Years)", type: "currency" },
      { id: "pensionGrowth30Yr", name: "Compounded Catch-up Pension (30 Years)", type: "currency" },
      { id: "taxSavingsGrowth10Yr", name: "Reinvested Tax Savings Value (10 Years)", type: "currency" },
      { id: "taxSavingsGrowth20Yr", name: "Reinvested Tax Savings Value (20 Years)", type: "currency" },
      { id: "taxSavingsGrowth30Yr", name: "Reinvested Tax Savings Value (30 Years)", type: "currency" }
    ],
    formula: "calculatePillar3aRetroactiveBuyIn"
  },
  {
    id: "box3-wealth-tax",
    name: "Box 3 Wealth Tax Calculator",
    slug: "box3-wealth-tax",
    category: "tax",
    description: "Calculate your Dutch Box 3 wealth tax for 2026 under the current transitional system and compare it to the proposed 2028 actual return system.",
    countries: ["nl"],
    inputs: [
      { id: "savings", name: "Bank Savings (€)", type: "currency", defaultValue: 100000, min: 0 },
      { id: "investments", name: "Investments & Other Assets (€)", type: "currency", defaultValue: 150000, min: 0 },
      { id: "debts", name: "Eligible Debts (€)", type: "currency", defaultValue: 10000, min: 0 },
      { id: "hasPartner", name: "Fiscal Partner?", type: "select", defaultValue: "no", options: [{ label: "No (Single)", value: "no" }, { label: "Yes (With Partner)", value: "yes" }] },
      { id: "actualSavingsReturn", name: "Actual Savings Interest (€)", type: "currency", defaultValue: 1500, min: 0 },
      { id: "actualInvestmentReturn", name: "Actual Investment Return (€)", type: "currency", defaultValue: 9000, min: 0 },
      { id: "actualDebtInterest", name: "Actual Debt Interest Paid (€)", type: "currency", defaultValue: 400, min: 0 }
    ],
    outputs: [
      { id: "taxableWealth2026", name: "2026 Taxable Wealth", type: "currency" },
      { id: "totalDeemedReturn2026", name: "2026 Deemed Return", type: "currency" },
      { id: "tax2026", name: "2026 Box 3 Tax (Current)", type: "currency" },
      { id: "actualReturn2028", name: "2028 Actual Return", type: "currency" },
      { id: "taxableReturn2028", name: "2028 Taxable Return", type: "currency" },
      { id: "tax2028", name: "2028 Box 3 Tax (Proposed)", type: "currency" },
      { id: "taxSavings", name: "Net Tax Savings (2026 vs 2028)", type: "currency" }
    ],
    formula: "calculateBox3WealthTax"
  },
  {
    id: "compulsory-kiwisaver-impact",
    name: "KiwiSaver Rate and Paycheck Scenario Calculator",
    slug: "compulsory-kiwisaver-impact",
    category: "retirement",
    description: "Compare today's 3.5% KiwiSaver rate with the enacted 4% rate for contributing members from April 2028 or hypothetical higher rates. Membership remains voluntary; model standard and all-in pay packages.",
    countries: ["nz"],
    inputs: [
      { id: "grossSalary", name: "Annual Salary or Total Package ($)", type: "currency", defaultValue: 75000, min: 0 },
      { id: "currentVoluntaryRate", name: "Current Employee Contribution (%)", type: "select", defaultValue: 3.5, options: [
        { label: "0% (Not contributing)", value: 0 },
        { label: "3.5% (2026 default)", value: 3.5 },
        { label: "4%", value: 4 },
        { label: "6%", value: 6 },
        { label: "8%", value: 8 },
        { label: "10% (Maximum)", value: 10 }
      ]},
      { id: "proposedScenario", name: "Comparison Rate (Assumes Participation)", type: "select", defaultValue: "gradual-8", options: [
        { label: "4% + 4% (enacted from April 2028)", value: "gradual-8" },
        { label: "5% + 5% (hypothetical future rate)", value: "gradual-10" },
        { label: "6% + 6% (National 2032 election pledge)", value: "gradual-12" }
      ]},
      { id: "remunerationType", name: "Employer Contribution Packaging", type: "select", defaultValue: "added", options: [
        { label: "Employer Contribution Added (Standard)", value: "added" },
        { label: "Fixed Total Package (includes employer contribution)", value: "deducted" }
      ]},
      { id: "annualReturn", name: "Expected Annual Fund Return (%)", type: "percent", defaultValue: 6.0, min: 0, max: 15 },
      { id: "currentAge", name: "Current Age (under 65)", type: "number", defaultValue: 35, min: 16, max: 64, tooltip: "The projection stops all modelled deposits at 65, then grows the existing balance only. You may be able to make voluntary member deposits after 65; those are outside this scenario." },
      { id: "termYears", name: "Savings Horizon (Years)", type: "number", defaultValue: 30, min: 1, max: 50 },
      { id: "currentBalance", name: "Current KiwiSaver Balance ($)", type: "currency", defaultValue: 15000, min: 0 }
    ],
    outputs: [
      { id: "proposedFinalBalance", name: "Projected Balance (Selected Rate)", type: "currency" },
      { id: "balanceDifference", name: "Difference in Projected Balance", type: "currency" },
      { id: "proposedPaycheck", name: "Annual Take-home (Selected Rate)", type: "currency" },
      { id: "paycheckImpact", name: "Annual Take-home Difference", type: "currency" },
      { id: "totalAccumulatedEmployerProposed", name: "Projected Employer Contributions", type: "currency" },
      { id: "projectedContributionYears", name: "Modelled contribution years before 65", type: "number" }
    ],
    formula: "calculateCompulsoryKiwiSaverImpact"
  },
  {
    id: "new-home-gst-rebate",
    name: "New Home GST/HST Relief Calculator",
    slug: "new-home-gst-rebate",
    category: "loans",
    description: "Calculate federal new-home GST relief and, for eligible Ontario purchases, ENHR plus the ONHAP top-up after the federal rebate offset.",
    countries: ["ca"],
    inputs: [
      { id: "homePrice", name: "New Home Price Before GST/HST (C$)", type: "currency", defaultValue: 850000, min: 0 },
      { id: "province", name: "Province of Purchase", type: "select", defaultValue: "other", options: [
        { label: "Other province (federal 5% component only)", value: "other" },
        { label: "Ontario (HST 13% + ENHR + ONHAP)", value: "on" }
      ]},
      { id: "isFirstTimeBuyer", name: "Are you a First-Time Homebuyer?", type: "select", defaultValue: "yes", options: [
        { label: "Yes, First-Time Buyer", value: "yes" },
        { label: "No, Repeat Buyer", value: "no" }
      ]},
      { id: "hbpAmount", name: "One person's RRSP withdrawal under HBP ($)", type: "currency", defaultValue: 60000, min: 0, max: 60000, tooltip: "The current individual HBP withdrawal limit is C$60,000. This input models one person's withdrawal and assumes HBP eligibility is confirmed separately." },
      { id: "marginalTaxRate", name: "Marginal Rate for HBP Tax Deferral (%)", type: "percent", defaultValue: 30, min: 0, max: 54 }
    ],
    outputs: [
      { id: "totalSavings", name: "Combined Housing-Tax Relief", type: "currency" },
      { id: "gstRebate", name: "Federal Housing Rebate", type: "currency" },
      { id: "enhancedOntario", name: "Ontario provincial housing rebate", type: "currency" },
      { id: "ontarioTopUp", name: "Ontario ONHAP After Federal Offset", type: "currency" },
      { id: "standardGst", name: "Modeled GST/HST portion before relief", type: "currency" },
      { id: "netGstPaid", name: "Modeled GST/HST portion after relief", type: "currency" },
      { id: "hbpTaxSavings", name: "Illustrative HBP Income Tax Deferred", type: "currency" }
    ],
    formula: "calculateCanadaNewHomeGstRebate"
  },
  {
    id: "aktivrente-tax-savings",
    name: "Aktivrente Tax Savings Calculator",
    slug: "aktivrente-tax-savings",
    category: "tax",
    description: "Calculate your tax savings under Germany's 2026 Aktivrente rule when working past statutory retirement age (earning up to €2,000/month tax-free).",
    countries: ["de"],
    inputs: [
      { id: "pensionIncome", name: "Annual Pension/Retirement Income (€)", type: "currency", defaultValue: 24000, min: 0 },
      { id: "monthlyActiveEarnings", name: "Active Work Earnings per Month (€)", type: "currency", defaultValue: 1500, min: 0 },
      { id: "churchTax", name: "Church Tax (Kirchensteuer)?", type: "select", defaultValue: "no", options: [{ label: "No", value: "no" }, { label: "Yes", value: "yes" }] }
    ],
    outputs: [
      { id: "grossPension", name: "Annual Pension", type: "currency" },
      { id: "grossActiveEarnings", name: "Annual Active Earnings", type: "currency" },
      { id: "taxFreeActiveEarnings", name: "Tax-Free Portion", type: "currency" },
      { id: "taxableActiveEarnings", name: "Taxable Portion", type: "currency" },
      { id: "taxWithoutTotal", name: "Tax Liability Without Aktivrente", type: "currency" },
      { id: "taxWithTotal", name: "Tax Liability With Aktivrente", type: "currency" },
      { id: "taxSavings", name: "Net Annual Tax Savings", type: "currency" },
      { id: "socialSecurityPaid", name: "Total Social Security Contributions", type: "currency" },
      { id: "netIncomeWith", name: "Net Annual Income (Post-Tax & Social Security)", type: "currency" }
    ],
    formula: "calculateAktivrenteTaxSavings"
  },
  {
    id: "fruehstart-rente-growth",
    name: "Frühstart-Rente Growth Projector",
    slug: "fruehstart-rente-growth",
    category: "retirement",
    description: "Project the future value of Germany's Frühstart-Rente, where the government contributes €10/month starting between ages 6 and 17 until age 18.",
    countries: ["de"],
    inputs: [
      { id: "childAge", name: "Child's Current Age (Years)", type: "number", defaultValue: 6, min: 6, max: 17, step: 1 },
      { id: "assumedReturn", name: "Expected Annual Return Rate (%)", type: "percent", defaultValue: 6.0, min: 0, max: 15 }
    ],
    outputs: [
      { id: "childAge", name: "Starting Child Age", type: "number" },
      { id: "yearsOfContribution", name: "Years of Government Contributions", type: "number" },
      { id: "totalContributions", name: "Total Government Contributions", type: "currency" },
      { id: "valueAt18", name: "Value at Age 18", type: "currency" },
      { id: "valueAt30", name: "Value at Age 30 (No new contributions)", type: "currency" },
      { id: "valueAt50", name: "Value at Age 50 (No new contributions)", type: "currency" },
      { id: "valueAt67", name: "Value at Age 67 (Statutory Retirement Age)", type: "currency" },
      { id: "totalInterestAt67", name: "Total Compounded Growth", type: "currency" },
      { id: "growthMultiplier", name: "Growth Multiplier (Capital Return)", type: "number" }
    ],
    formula: "calculateFruehstartRenteGrowth"
  },
  {
    id: "ups-pension-estimator",
    name: "UPS Pension Estimator",
    slug: "ups-pension-estimator",
    category: "retirement",
    description: "Estimate your guaranteed monthly pension, family pension, and lump sum under India's Unified Pension Scheme (UPS) based on pay and service.",
    countries: ["in"],
    inputs: [
      { id: "yearsOfService", name: "Years of Service", type: "number", defaultValue: 25, min: 1, max: 45, step: 1 },
      { id: "lastBasicPay", name: "Last Basic Pay (Monthly) (₹)", type: "number", defaultValue: 50000, min: 10000, max: 1000000, step: 1000 },
      { id: "daRate", name: "Dearness Allowance (DA) Rate (%)", type: "percent", defaultValue: 50, min: 0, max: 200, step: 1 }
    ],
    outputs: [
      { id: "yearsOfService", name: "Years of Service", type: "number" },
      { id: "lastBasicPay", name: "Last Monthly Basic Pay", type: "currency" },
      { id: "daRate", name: "Dearness Allowance (DA) Rate", type: "percent" },
      { id: "monthlyEmoluments", name: "Monthly Emoluments (Basic + DA)", type: "currency" },
      { id: "guaranteedPension", name: "Guaranteed Monthly Pension", type: "currency" },
      { id: "familyPension", name: "Assured Family Pension", type: "currency" },
      { id: "lumpSum", name: "Retirement Lump Sum Payout", type: "currency" }
    ],
    formula: "calculateUpsPension"
  },
  {
    id: "lrs-tcs-calculator",
    name: "LRS TCS Calculator (Budget 2026)",
    slug: "lrs-tcs-calculator",
    category: "tax",
    description: "Calculate TCS on foreign remittances under India's Liberalised Remittance Scheme. Budget 2026 reduced education/medical TCS to 2% and simplified tour packages to flat 2%.",
    countries: ["in"],
    inputs: [
      { id: "remittanceAmount", name: "Remittance Amount (₹)", type: "currency", defaultValue: 500000, min: 0 },
      { id: "purpose", name: "Purpose of Remittance", type: "select", defaultValue: "education", options: [
        { label: "Education (self-funded)", value: "education" },
        { label: "Education (financed by recognized loan)", value: "education_loan" },
        { label: "Medical treatment", value: "medical" },
        { label: "Overseas tour package", value: "tour_package" },
        { label: "Investment / Gift / Other", value: "other" }
      ]},
      { id: "cumulativeFYRemittances", name: "Total LRS Remittances This FY So Far (₹)", type: "currency", defaultValue: 0, min: 0, tooltip: "Sum of all your LRS outward remittances in this financial year across all banks" }
    ],
    outputs: [
      { id: "remittanceAmount", name: "Remittance Amount", type: "currency" },
      { id: "tcsRate", name: "Applicable TCS Rate", type: "percent" },
      { id: "taxableAmount", name: "Amount Subject to TCS", type: "currency" },
      { id: "tcsAmount", name: "TCS to Be Collected", type: "currency" },
      { id: "effectiveOutflow", name: "Total Cash Outflow (Remittance + TCS)", type: "currency" },
      { id: "thresholdRemaining", name: "Remaining Headroom Before ₹10L Threshold", type: "currency" },
      { id: "tcsCreditable", name: "TCS Creditable Status", type: "text" }
    ],
    formula: "calculateLrsTcs"
  },
  {
    id: "irs-penalty-relief-aep",
    name: "IRS Penalty Relief & AEP Eligibility Calculator",
    slug: "irs-penalty-relief-aep",
    category: "tax",
    description: "Determine your eligibility for the new 2026 IRS Automatic Exemption from Penalty (AEP) program and estimate waiver savings.",
    countries: ["us"],
    inputs: [
      { id: "unpaidTax", name: "Unpaid Tax Amount ($)", type: "currency", defaultValue: 5000, min: 0 },
      { id: "monthsLate", name: "Number of Months Late", type: "number", defaultValue: 3, min: 1, max: 120, step: 1 },
      { id: "penaltyType", name: "Type of Penalty", type: "select", defaultValue: "both", options: [
        { label: "Both Failure to File & Failure to Pay", value: "both" },
        { label: "Failure to File Only", value: "file" },
        { label: "Failure to Pay Only", value: "pay" },
        { label: "Failure to Deposit (Businesses)", value: "deposit" }
      ]},
      { id: "hasCleanHistory", name: "Clean Filing/Payment History (Past 3 Years)?", type: "select", defaultValue: "yes", options: [
        { label: "Yes, no penalties in past 3 years", value: "yes" },
        { label: "No, had penalties or late filings", value: "no" }
      ]},
      { id: "depositDaysLate", name: "Days Late (Deposit Only)", type: "number", defaultValue: 10, min: 1, max: 365, step: 1 }
    ],
    outputs: [
      { id: "standardPenalty", name: "Standard Accrued IRS Penalty", type: "currency" },
      { id: "reliefAmount", name: "Estimated AEP Relief Amount", type: "currency" },
      { id: "netPenaltyOwed", name: "Net Penalty Owed after AEP", type: "currency" },
      { id: "isEligibleText", name: "AEP Eligibility Status", type: "text" }
    ],
    formula: "calculateIrsPenaltyReliefAep"
  },
  {
    id: "va-partial-claim-estimator",
    name: "VA Partial Claim Estimator",
    slug: "va-partial-claim-estimator",
    category: "loans",
    description: "Estimate how much the VA can advance to cure your delinquent VA-backed mortgage arrears under the new Partial Claim Program (0% interest, repaid at sale).",
    countries: ["us"],
    inputs: [
      { id: "unpaidPrincipal", name: "Unpaid Principal Balance ($)", type: "currency", defaultValue: 300000, min: 0 },
      { id: "arrearageAmount", name: "Total Arrearage Amount Owed ($)", type: "currency", defaultValue: 15000, min: 0 },
      { id: "hasCovidArrears", name: "Are any arrears from the COVID-era (Mar 2020 – May 2025)?", type: "select", defaultValue: "no", options: [
        { label: "No — standard arrears", value: "no" },
        { label: "Yes — includes COVID-era arrears", value: "yes" }
      ]},
      { id: "trialPlanCompleted", name: "Have you completed a 3-month trial payment plan?", type: "select", defaultValue: "yes", options: [
        { label: "Yes, completed", value: "yes" },
        { label: "No, not yet started", value: "no" }
      ]}
    ],
    outputs: [
      { id: "estimatedClaimAmount", name: "Estimated VA Partial Claim Amount", type: "currency" },
      { id: "maxCapAmount", name: "Maximum Possible Claim (Cap)", type: "currency" },
      { id: "arrearageAmount", name: "Total Arrearage Owed", type: "currency" },
      { id: "isCappedText", name: "Cap Status", type: "text" },
      { id: "remainingArrears", name: "Remaining Arrears After Claim", type: "currency" },
      { id: "trialPlanRequired", name: "Trial Payment Plan Status", type: "text" }
    ],
    formula: "calculateVaPartialClaim"
  },
  {
    id: "uk-bnpl-dpc-rights",
    name: "UK Buy Now Pay Later Consumer Rights Simulator",
    slug: "uk-bnpl-dpc-rights",
    category: "loans",
    description: "Check your consumer protections, affordability requirements, and late fees under the UK's July 2026 Deferred Payment Credit rules.",
    countries: ["gb"],
    inputs: [
      { id: "purchaseAmount", name: "BNPL Purchase Amount (£)", type: "currency", defaultValue: 500, min: 0 },
      { id: "repaymentTermMonths", name: "Repayment Term (Months)", type: "number", defaultValue: 3, min: 1, max: 12, step: 1 },
      { id: "missedPayments", name: "Number of Missed Payments", type: "number", defaultValue: 1, min: 0, max: 12, step: 1 },
      { id: "provider", name: "BNPL Provider", type: "select", defaultValue: "klarna", options: [
        { label: "Klarna", value: "klarna" },
        { label: "Clearpay", value: "clearpay" },
        { label: "PayPal Pay in 3", value: "paypal" },
        { label: "Other DPC Provider", value: "other" }
      ]},
      { id: "purchaseDate", name: "Purchase Date", type: "select", defaultValue: "post", options: [
        { label: "On or after July 15, 2026 (FCA Regulated)", value: "post" },
        { label: "Before July 15, 2026 (Unregulated)", value: "pre" }
      ]}
    ],
    outputs: [
      { id: "totalLateFees", name: "Accumulated Late Fees", type: "currency" },
      { id: "totalRepayment", name: "Total Repayment Cost", type: "currency" },
      { id: "regulationStatus", name: "FCA Regulation Status", type: "text" },
      { id: "fosAccess", name: "Financial Ombudsman Service (FOS) Access", type: "text" },
      { id: "creditCheckType", name: "FCA Credit Check Mandate", type: "text" },
      { id: "consumerDuty", name: "Consumer Duty Protection", type: "text" }
    ],
    formula: "calculateUkBnplDpcRights"
  },
  {
    id: "kvdr-9-10-rule-eligibility-calculator",
    name: "KVdR 9/10 Rule Eligibility Calculator",
    slug: "kvdr-9-10-rule-eligibility-calculator",
    category: "retirement",
    description: "Calculate your Krankenversicherung der Rentner (KVdR) eligibility. Model GKV vs PKV years, check the 9/10 rule, and apply children's credits.",
    countries: ["de"],
    inputs: [
      { id: "workingStartAge", name: "Age of First Employment", type: "number", defaultValue: 20, min: 15, max: 50 },
      { id: "retirementAge", name: "Planned Retirement Age", type: "number", defaultValue: 67, min: 55, max: 75 },
      { id: "gkvYearsSecondHalf", name: "Actual GKV Years in Second Half of Career", type: "number", defaultValue: 15, min: 0, max: 30, tooltip: "Count compulsory, voluntary and family GKV coverage after your career midpoint. This cannot exceed the length of the second half; exact dates determine the legal result." },
      { id: "childrenCount", name: "Number of Children", type: "number", defaultValue: 0, min: 0, max: 10, tooltip: "Adds 3 GKV credit years per child for both parents." }
    ],
    outputs: [
      { id: "totalWorkingLife", name: "Total Working Career (Years)", type: "number" },
      { id: "secondHalfStart", name: "Second Half Midpoint (Age)", type: "number" },
      { id: "requiredGkvYears", name: "Required GKV Years (9/10 of Second Half)", type: "number" },
      { id: "actualGkvYears", name: "Actual GKV Years in Second Half", type: "number" },
      { id: "childCredits", name: "Child GKV Credit Years", type: "number" },
      { id: "totalGkvYears", name: "Virtual GKV Years for 9/10 Rule", type: "number" },
      { id: "isEligibleText", name: "KVdR Eligibility Status", type: "text" },
      { id: "yearsShort", name: "GKV Years Short", type: "number" }
    ],
    formula: "calculateKvdrEligibility"
  },
  {
    id: "whm-tax-take-home-calculator",
    name: "Working Holiday Maker Tax Calculator",
    slug: "whm-tax-take-home-calculator",
    category: "tax",
    description: "Calculate your net take-home pay, ATO tax withholding, and superannuation DASP refund as a subclass 417 or 462 Working Holiday Maker in Australia.",
    countries: ["au"],
    inputs: [
      { id: "wageType", name: "Wage Input Type", type: "select", defaultValue: "hourly", options: [
        { label: "Hourly Wage", value: "hourly" },
        { label: "Weekly Wage", value: "weekly" }
      ]},
      { id: "wageAmount", name: "Wage Amount ($)", type: "number", defaultValue: 30, min: 0, max: 10000 },
      { id: "hoursPerWeek", name: "Hours Worked Per Week", type: "number", defaultValue: 38, min: 0, max: 168 },
      { id: "employerRegistered", name: "Employer Registered Status", type: "select", defaultValue: "registered", options: [
        { label: "Registered WHM Employer (15% rate)", value: "registered" },
        { label: "Unregistered Employer (30% resident rate)", value: "unregistered" }
      ]}
    ],
    outputs: [
      { id: "weeklyGrossPay", name: "Weekly Gross Pay", type: "currency" },
      { id: "weeklyNetPay", name: "Weekly Net Pay (Take-Home)", type: "currency" },
      { id: "weeklyTaxWithheld", name: "Weekly Tax Withheld", type: "currency" },
      { id: "annualGrossPay", name: "Annual Gross Pay", type: "currency" },
      { id: "annualNetPay", name: "Annual Net Pay (Take-Home)", type: "currency" },
      { id: "annualTaxWithheld", name: "Annual Tax Withheld", type: "currency" },
      { id: "annualSuperContribution", name: "Employer Super Contribution (12.0%)", type: "currency" },
      { id: "daspTax", name: "DASP Withholding Tax (65%)", type: "currency" },
      { id: "daspSuperRefund", name: "Net DASP Super Refund", type: "currency" },
      { id: "residentTaxComparison", name: "Standard Resident Tax", type: "currency" },
      { id: "residentNetComparison", name: "Standard Resident Net Take-Home", type: "currency" },
      { id: "takeHomeDifference", name: "Net Income Difference vs Resident", type: "currency" }
    ],
    formula: "calculateWhmTaxTakeHome"
  },
  {
    id: "overseas-student-loan-repayment-calculator",
    name: "Overseas Student Loan Repayment Calculator",
    slug: "overseas-student-loan-repayment-calculator",
    category: "loans",
    description: "Amortize your New Zealand student loan payoff duration, total interest, and assessment obligations under the 2026 overseas interest rules (5.6%).",
    countries: ["nz"],
    inputs: [
      { id: "loanBalance", name: "Current Student Loan Balance ($)", type: "number", defaultValue: 25000, min: 0, max: 500000 },
      { id: "annualExtraRepayments", name: "Annual Extra Voluntary Payments ($)", type: "number", defaultValue: 1000, min: 0, max: 100000 }
    ],
    outputs: [
      { id: "currentInterestRate", name: "Overseas Interest Rate", type: "text" },
      { id: "compulsoryAnnualRepayment", name: "Required Annual Repayment", type: "currency" },
      { id: "totalRepaymentYearly", name: "Total Annual Repayments", type: "currency" },
      { id: "interestChargedFirstYear", name: "Interest Charged (First Year)", type: "currency" },
      { id: "payoffDuration", name: "Estimated Time to Payoff (Years)", type: "number" },
      { id: "totalRepaymentsPaid", name: "Total Repayments Made", type: "currency" },
      { id: "totalInterestAccrued", name: "Total Interest Paid Over Lifetime", type: "currency" },
      { id: "payoffStatus", name: "Loan Payoff Status", type: "text" }
    ],
    formula: "calculateOverseasStudentLoanRepayment"
  },
  {
    id: "zzp-tax-deduction-take-home-calculator",
    name: "ZZP Tax Deduction & Take-Home Calculator",
    slug: "zzp-tax-deduction-take-home-calculator",
    category: "tax",
    description: "Calculate your net profit, entrepreneurial tax deductions (Zelfstandigenaftrek, Startersaftrek, MKB), Box 1 income tax, and ZVW contributions in the Netherlands.",
    countries: ["nl"],
    inputs: [
      { id: "grossRevenues", name: "Annual Gross Revenues (€)", type: "number", defaultValue: 60000, min: 0, max: 1000000 },
      { id: "businessExpenses", name: "Annual Business Expenses (€)", type: "number", defaultValue: 10000, min: 0, max: 500000 },
      { id: "meetUrencriterium", name: "Meet 1,225 Hours Rule (Urencriterium)", type: "select", defaultValue: "yes", options: [{ label: "Yes", value: "yes" }, { label: "No", value: "no" }] },
      { id: "isStarter", name: "Eligible for Starters Allowance (Startersaftrek)", type: "select", defaultValue: "no", options: [{ label: "Yes", value: "yes" }, { label: "No", value: "no" }] }
    ],
    outputs: [
      { id: "grossProfit", name: "Gross Business Profit", type: "currency" },
      { id: "zelfstandigenaftrek", name: "Self-Employed Deduction (Zelfstandigenaftrek)", type: "currency" },
      { id: "startersaftrek", name: "Starters Deduction (Startersaftrek)", type: "currency" },
      { id: "mkbWinstvrijstelling", name: "SME Profit Exemption (MKB, 12.7%)", type: "currency" },
      { id: "taxableProfit", name: "Taxable Profit (Box 1 Base)", type: "currency" },
      { id: "box1Tax", name: "Box 1 Income Tax & Social Premiums", type: "currency" },
      { id: "zvwContribution", name: "Healthcare Contribution (ZVW, 4.85%)", type: "currency" },
      { id: "totalTax", name: "Total Taxes & Contributions Paid", type: "currency" },
      { id: "totalDeductions", name: "Total Tax Deductions Claimed", type: "currency" },
      { id: "netIncomeAnnual", name: "Annual Net Take-Home Pay", type: "currency" },
      { id: "netIncomeMonthly", name: "Monthly Net Take-Home Pay", type: "currency" }
    ],
    formula: "calculateZzpTaxDeductionTakeHome"
  },
  {
    id: "new-labour-codes-ctc-restructuring-calculator",
    name: "India Labour Codes Wage Base and PF Checker",
    slug: "new-labour-codes-ctc-restructuring-calculator",
    category: "tax",
    description: "Add up your own salary components under the Labour Codes' 50% excluded-remuneration test and inspect a capped EPFO and gratuity scenario without inventing a compulsory CTC restructure.",
    countries: ["in"],
    inputs: [
      { id: "includedWagesMonthly", name: "Monthly basic + DA + retaining allowance (₹)", type: "currency", defaultValue: 40000, min: 0, max: 5000000, tooltip: "Enter the included wage components on your own salary annexure. The law does not automatically rewrite the Basic line to half your CTC." },
      { id: "excludedCashMonthly", name: "Monthly cash allowances excluded from wages (₹)", type: "currency", defaultValue: 50000, min: 0, max: 5000000, tooltip: "Use the monthly equivalent of excluded cash items, such as HRA, conveyance or overtime, after checking their treatment under section 2(y)." },
      { id: "employerPfMonthly", name: "Employer PF/pension in remuneration test (₹/month)", type: "currency", defaultValue: 3000, min: 0, max: 5000000, tooltip: "Enter the employer PF or pension amount from your package. The Ministry includes it for the 50% remuneration test but excludes gratuity and ESI." },
      { id: "statutoryBonusMonthly", name: "Statutory bonus, monthly equivalent (₹)", type: "currency", defaultValue: 0, min: 0, max: 5000000, tooltip: "Enter only statutory bonus included in the 50% remuneration test, divided by 12 for a monthly scenario; do not enter discretionary annual incentives." },
      { id: "pfCoverage", name: "Are you already covered by EPFO?", type: "select", defaultValue: "yes", options: [{ label: "Yes, EPFO member", value: "yes" }, { label: "No / not confirmed", value: "no" }], tooltip: "The tool does not determine your enrollment. The ₹25,000 wage ceiling expanded mandatory coverage from 17 September 2026, subject to other scheme rules." },
      { id: "pfRate", name: "Your establishment's EPF contribution rate", type: "select", defaultValue: 12, options: [{ label: "12% standard", value: 12 }, { label: "10% eligible establishment", value: 10 }], tooltip: "EPFO lists a 10% rate for specific establishments. Confirm your employer's applicable rate before choosing it." },
      { id: "pfContributionMode", name: "PF contribution wage treatment", type: "select", defaultValue: "statutory_cap", options: [{ label: "Statutory ₹25,000 monthly wage ceiling", value: "statutory_cap" }, { label: "Higher-wage PF already agreed", value: "higher_agreed" }], tooltip: "Choose the full-wage option only if you and your employer have an applicable higher-wage contribution arrangement." }
    ],
    outputs: [
      { id: "remunerationForTest", name: "Monthly remuneration used in 50% test", type: "currency" },
      { id: "excludedForTest", name: "Excluded remuneration entered", type: "currency" },
      { id: "allowanceAddBack", name: "Excluded amount added back to code wages", type: "currency" },
      { id: "codeWageBase", name: "Computed monthly code wage base", type: "currency" },
      { id: "wageSharePercent", name: "Code wage share of test remuneration", type: "percent" },
      { id: "grossCashMonthly", name: "Monthly-equivalent gross cash entered", type: "currency" },
      { id: "pfContributionWage", name: "PF contribution wage in selected scenario", type: "currency" },
      { id: "employeePfMonthly", name: "Illustrative monthly employee PF deduction", type: "currency" },
      { id: "employerEpfoMonthly", name: "Illustrative monthly employer EPFO share", type: "currency" },
      { id: "cashAfterEmployeePfBeforeTax", name: "Cash after employee PF, before tax and other deductions", type: "currency" },
      { id: "oneYearGratuityReference", name: "One completed-year gratuity reference", type: "currency" },
      { id: "wageRuleNotice", name: "What the 50% wage rule does", type: "text" },
      { id: "pfNotice", name: "EPFO contribution assumption", type: "text" },
      { id: "gratuityNotice", name: "Gratuity limitation", type: "text" }
    ],
    formula: "calculateNewLabourCodesCtcRestructuring"
  }
];

function titleToSearchCore(name: string): string {
  return name
    .replace(/\s+calculator$/i, "")
    .replace(/\s+tool$/i, "")
    .trim()
    .toLowerCase();
}

function titleToDisplayCore(name: string): string {
  return name
    .replace(/\s+calculator$/i, "")
    .replace(/\s+tool$/i, "")
    .trim();
}

const CUSTOM_RELATED_TOOLS: Record<string, string[]> = {

  "inflation-impact": [],

  "ssb-calculator": []

};

function getRelatedCalculatorSlugs(calc: CalculatorConfig): string[] {
  if (CUSTOM_RELATED_TOOLS[calc.slug]) {
    return CUSTOM_RELATED_TOOLS[calc.slug];
  }
  const calcCountries = calc.countries === "all" ? null : calc.countries;
  return baseCalculatorRegistry
    .filter((candidate) => {
      if (candidate.category !== calc.category || candidate.slug === calc.slug) return false;
      if (calcCountries === null) return candidate.countries === "all";
      return candidate.countries === "all" || calcCountries.some((country) => candidate.countries.includes(country));
    })
    .slice(0, 5)
    .map((candidate) => candidate.slug);
}

const CUSTOM_KEYWORDS: Record<string, string[]> = {

  "offset-error-cost-calculator": [
    "offset account not linked compensation", "asic rep 837 offset accounts", "offset account error refund australia",
    "interest lost offset not linked", "bank offset account failure claim", "mortgage offset compensation afca"
  ],

  "capital-one-360-settlement-payout-estimator": [
    "capital one 360 settlement calculator", "capital one settlement how much will i get", "capital one 425 million settlement payout",
    "360 savings class action payment date", "capital one settlement appeal delay", "360 savings vs performance savings gap"
  ],

  "ssb-calculator": [
    "singapore savings bonds calculator", "ssb interest rate step up", "ssb vs fixed deposit 2026",
    "ssb redemption how long", "singapore savings bonds 200k limit", "ssb ladder strategy"
  ],

  "compulsory-kiwisaver-impact": [
    "compulsory kiwisaver debate", "kiwisaver from age 18 calculator", "cost of delaying kiwisaver",
    "kiwisaver opt out impact", "nz retirement savings gap", "kiwisaver vs australian super"
  ],

  "payday-super-impact": [
    "payday super july 2026", "super paid every payday", "payday super calculator",
    "quarterly super vs payday super", "super guarantee 7 business days", "unpaid super protection 2026"
  ],

  "payday-super-compliance": [
    "payday super penalties employer", "SGC calculator 2026", "late super payment penalty",
    "superannuation guarantee charge uplift", "director penalty notice super", "super 7 day deadline ATO"
  ],

  "budget-2026-cpf-top-up-checker": [
    "budget 2026 cpf top up", "$1500 cpf top up eligibility", "cpf bonus december 2026",
    "cpf top up annual value 21000", "basic retirement sum 110200", "singapore budget 2026 seniors"
  ],

  "cpf-retirement-age-transition-calculator-2026": [
    "retirement age 64 singapore", "re-employment age 69 july 2026", "cpf extra interest 55",
    "cpf life deferral increase", "cpf interest calculator 2026", "singapore statutory retirement age change"
  ],

  "aktivrente-tax-savings": [
    "Aktivrente 2000 euro steuerfrei", "working pensioner tax free germany", "Aktivrente calculator 2026",
    "tax free earnings after retirement germany", "Aktivrente social insurance", "rentner arbeiten steuerfrei"
  ],

  "box3-wealth-tax": [
    "box 3 tax calculator 2026", "vermogensbelasting netherlands", "deemed return box 3",
    "box 3 2028 actual return tax", "tegenbewijsregeling actual return", "wealth tax netherlands savings investments"
  ],

  "zzp-tax-deduction-take-home-calculator": [
    "zzp tax calculator 2026", "zelfstandigenaftrek 2026", "freelancer take home netherlands",
    "urencriterium 1225 hours", "MKB winstvrijstelling 12.7", "zzp netto inkomen berekenen"
  ],

  "kvdr-9-10-rule-eligibility-calculator": [
    "KVdR 9/10 Regelung rechner", "Krankenversicherung der Rentner eligibility", "9/10 rule german health insurance",
    "PKV zurück in GKV rente", "KVdR Kinder anrechnung 3 jahre", "freiwillig versichert rentner beitrag"
  ],

  "fruehstart-rente-growth": [
    "Frühstart-Rente rechner", "10 euro kinderdepot staat", "Frühstartrente ab 2026",
    "altersvorsorgedepot kinder", "fruehstart rente wie funktioniert", "state pension deposit children germany"
  ],

  "altersvorsorgedepot-calculator-2027": [
    "Altersvorsorgedepot 2027 rechner", "Riester Nachfolger depot", "Grundzulage 540 euro",
    "Kinderzulage 300 euro depot", "Altersvorsorgereformgesetz 2026", "riester replacement germany ETF"
  ],

  "pillar-3a-retroactive-buy-in": [
    "pillar 3a buy in 2026", "3a nachzahlung rechner", "pillar 3a gap years 2025",
    "retroactive 3a contributions switzerland", "CHF 7258 limit 2026", "3a Einkaufslücke steuern sparen"
  ],

  "13th-ahv-pension-calculator-2026": [
    "13th AHV pension december 2026", "13. AHV rente rechner", "AHV 13th payment amount",
    "Plafonierung married couple AHV", "CHF 2520 maximum pension", "13th OASI payment switzerland"
  ],

  "instant-deduction": [
    "$1000 instant tax deduction australia", "standard work deduction 2026-27", "no receipts tax claim ATO",
    "instant deduction vs actual expenses", "work related expenses $1000", "australia tax deduction without receipts"
  ],

  "whm-tax-take-home-calculator": [
    "working holiday maker tax calculator", "backpacker tax australia 15%", "417 visa tax rate",
    "DASP superannuation refund 65%", "registered WHM employer check", "working holiday tax return australia"
  ],

  "stage-3-tax-cut-15-percent-bracket-calculator-2026": [
    "15 per cent tax bracket july 2026", "australia tax cut 2026-27", "$268 tax cut calculator",
    "instant asset write-off $20,000 permanent", "sole trader tax savings 2026", "new tax rates 2026-27 australia"
  ],

  "hra-exemption-calculator-8-cities": [
    "HRA exemption calculator FY 2026-27", "8 city 50 percent HRA rule", "bengaluru HRA metro status",
    "HRA exemption old regime", "rent receipts landlord PAN rule", "section 10 13A exemption calculator"
  ],

  "lrs-tcs-calculator": [
    "TCS on foreign remittance 2026", "LRS 10 lakh threshold", "TCS education loan exemption",
    "tour package TCS 2 percent", "section 206C(1G) calculator", "TCS refund form 26AS"
  ],

  "fair-work-minimum-wage-increase-calculator-july-2026": [
    "minimum wage increase july 2026", "$26.44 per hour minimum wage", "4.75% award increase calculator",
    "casual loading pay rise 2026", "fair work annual wage review 2026", "new award rates 1 july 2026"
  ],

  "new-labour-codes-ctc-restructuring-calculator": [
    "labour codes 50% wage add-back", "India CTC wage definition checker", "code on wages excluded allowances",
    "EPFO 25000 wage ceiling 2026", "PF contribution cap September 2026", "salary components code wage calculation"
  ],

  "ups-pension-estimator": [
    "unified pension scheme calculator", "UPS pension 50 percent basic pay", "UPS vs NPS which is better",
    "assured pension central government", "UPS minimum pension 10000", "UPS family pension 60 percent"
  ],

  "no-tax-on-tips": [
    "no tax on tips deduction", "tip income tax deduction 2026", "OBBBA tips deduction calculator",
    "are tips tax free now", "qualified tip income rules", "tips deduction phase out $150,000"
  ],

  "no-tax-on-overtime": [
    "no tax on overtime deduction", "overtime premium tax deduction", "OBBBA overtime calculator",
    "is overtime tax free 2026", "FLSA premium deduction W-2", "overtime deduction $12,500 cap"
  ],

  "senior-deduction": [
    "$6,000 senior deduction 2026", "OBBBA senior tax deduction", "extra deduction age 65",
    "senior deduction phase out $75,000", "social security tax relief seniors", "senior bonus deduction calculator"
  ],

  "auto-loan-interest-deduction": [
    "car loan interest tax deduction 2026", "auto loan interest deduction US assembled",
    "OBBBA vehicle interest deduction", "is car loan interest deductible now",
    "VIN final assembly deduction rule", "new car tax deduction $10,000"
  ],

  "obbba-total-savings": [
    "OBBBA tax savings calculator", "one big beautiful bill deductions", "tips overtime senior auto deduction",
    "combined OBBBA deductions 2026", "new 2025 tax deductions stack", "OBBBA phase out thresholds"
  ],

  "irs-penalty-relief-aep": [
    "IRS automatic penalty relief 2026", "AEP penalty waiver FS-2026-12", "failure to file penalty calculator",
    "first time abate replacement", "IRS late filing penalty waived", "clean compliance history 3 years"
  ],

  "irs-mileage-rate-calculator-2026": [
    "IRS mileage rate 2026", "76 cents per mile july 2026", "split year mileage deduction",
    "business mileage rate increase 2026", "standard mileage vs actual expenses", "mileage log H1 H2 2026"
  ],

  "401k-contribution-calculator-2026": [
    "401k contribution limit 2026", "super catch-up 60 to 63", "$24,500 deferral limit",
    "per paycheck 401k calculator", "Roth catch-up mandate $150,000", "maximize employer match 2026"
  ],

  "uk-bnpl-dpc-rights": [
    "BNPL late fees UK", "buy now pay later FCA regulation", "deferred payment credit rules 2026",
    "klarna missed payment fee", "section 75 BNPL protection", "BNPL credit score impact"
  ],

  "new-home-gst-rebate": [
    "first time home buyer GST rebate canada", "bill c-4 gst rebate calculator",
    "ontario enhanced new housing rebate", "new build HST rebate ontario",
    "GST rebate new home 2026", "home buyers plan stacking"
  ],

  "va-partial-claim-estimator": [
    "VA partial claim program 2026", "VA mortgage arrears help", "VA loan behind on payments",
    "VA partial claim calculator", "VASP replacement program", "VA loss mitigation options"
  ],

  "apra-dti-cap-check": [
    "APRA DTI cap 2026", "debt to income ratio home loan australia", "6x income lending limit",
    "high DTI mortgage australia", "APRA lending rules february 2026", "borrowing capacity DTI calculator"
  ],

  "overseas-student-loan-repayment-calculator": [
    "NZ student loan overseas", "student loan 184 day rule", "IRD overseas based borrower",
    "nz student loan interest 5.6", "student loan repayment overseas calculator", "kiwi expat student loan"
  ],

  "student-loan-save-transition-calculator-2026": [
    "SAVE plan ending what to do", "repayment assistance plan calculator", "RAP student loan payment",
    "SAVE to RAP transition 2026", "student loan 90 day window", "IBR vs RAP comparison"
  ],

  "rbi-mis-selling-compensation-calculator-2026": [
    "bank forced insurance with loan", "RBI mis-selling complaint", "loan insurance bundling refund",
    "RB-IOS ombudsman compensation", "ULIP forced with home loan", "bancassurance mis-selling india"
  ],

  "osfi-mortgage-stress-test-exemption-switch-calculator-2026": [
    "mortgage stress test switch lenders", "OSFI straight switch exemption", "mortgage renewal stress test 2026",
    "switch mortgage without stress test canada", "minimum qualifying rate exemption", "mortgage renewal shopping canada"
  ],

  "fma-cccfa-complaint-eligibility-checker-july-2026": [
    "FMA CCCFA complaint", "irresponsible lending complaint nz", "FMA consumer credit regulator",
    "predatory lender complaint new zealand", "FSCL IFSO dispute scheme", "CCCFA borrower rights 2026"
  ],
  "nz-fees-free-2026-completion-cutoff-calculator": [
    "fees free 2026 completion deadline nz", "fees free no transitional arrangements 2026",
    "fees free finish in 2027 amount lost", "final year fees free one EFTS cap",
    "fees free work based training completion cutoff", "fees free claim after 2026 completion"
  ],
  "au-pension-supplement-overseas-12-week-calculator": [
    "pension supplement overseas 12 weeks 2026", "Centrelink pension supplement travel September 2026",
    "DVA supplement 84 days abroad", "pension supplement permanent overseas move",
    "pension supplement trip payment estimate", "age pension overseas supplement cutoff"
  ],
  "california-ab1847-wildfire-forbearance-planner": [
    "California AB 1847 forbearance 24 months", "LA wildfire mortgage forbearance 2027",
    "AB 1847 prior forbearance counts toward cap", "Eaton Palisades mortgage forbearance extension",
    "wildfire mortgage paused payments still owed", "California mortgage forbearance investor deferral"
  ],

  "irs-afr-intra-family-loan-calculator-august-2026": [
    "applicable federal rate august 2026", "intra family loan interest rate",
    "AFR imputed interest calculator", "family loan gift tax rules",
    "IRC section 7872 minimum interest", "lending money to family IRS rules"
  ],

  "cash-isa-2027-cap-impact": [
    "cash ISA cap 2027", "£12000 cash ISA limit", "ISA allowance change april 2027",
    "cash ISA rules under 65", "ISA reform autumn budget", "cash ISA tax impact calculator"
  ],

  "trump-account-growth": [
    "trump account calculator", "section 530A account growth", "trump account $1000 seed",
    "trump account vs 529", "child investment account OBBBA", "trump account tax rules"
  ],

  "inflation-impact": [
    "inflation calculator", "purchasing power loss calculator", "value of money over time inflation",
    "inflation impact estimator", "future value of cash inflation", "how does inflation affect my savings",
    "inflation adjusted value calculator"
  ]

};

const CUSTOM_SEARCH_INTENTS: Record<string, string[]> = {

  "california-ab1847-wildfire-forbearance-planner": [
    "work out cap room after earlier LA wildfire mortgage forbearance",
    "see how many planned months fit under AB 1847's 24-month limit",
    "estimate mortgage payments postponed during a requested extension"
  ],

  "nz-fees-free-2026-completion-cutoff-calculator": [
    "check the cost if my NZ qualification finishes in 2027",
    "estimate final-year Fees Free at risk after the 2026 cutoff",
    "see whether a January completion changes my expected Fees Free amount"
  ],

  "au-pension-supplement-overseas-12-week-calculator": [
    "estimate my Pension Supplement for a 14-week overseas visit",
    "see how the 12-week travel rule affects my own payment amount",
    "work out supplement cash flow before a temporary trip abroad"
  ],

  "offset-error-cost-calculator": ["work out extra interest paid while my offset was not linked", "check if my bank owes me offset account compensation", "put a dollar figure on an offset error before calling the bank"],

  "capital-one-360-settlement-payout-estimator": ["estimate my capital one 360 savings settlement payment", "work out interest lost on a 360 savings account", "see how the $425 million fund splits pro rata"],

  "inflation-impact": ["how much will inflation reduce my purchasing power", "calculate future value of money adjusted for inflation", "how much is $100 worth in 10 years due to inflation"],

  "ssb-calculator": ["calculate singapore savings bond returns", "estimate SSB step-up interest schedule", "compare SSB vs T-bills and fixed deposits"]
};

// PRD 01 Epic I1: keywords are MANUAL ONLY. The old buildCalculatorKeywords
// generator ("how to calculate {x}", "{x} estimator" templates) was removed;
// calculators without hand-written CUSTOM_KEYWORDS ship no keywords at all.
function buildSearchIntents(calc: CalculatorConfig): string[] {
  if (CUSTOM_SEARCH_INTENTS[calc.slug]) {
    return CUSTOM_SEARCH_INTENTS[calc.slug];
  }
  const core = titleToSearchCore(calc.name);
  return [
    `how do I calculate ${core}`,
    `estimate my ${core}`,
    `compare ${core} scenarios`
  ];
}

export const calculatorRegistry: CalculatorConfig[] = baseCalculatorRegistry.map((calc) => ({
  ...calc,
  keywords: calc.keywords ?? CUSTOM_KEYWORDS[calc.slug],
  relatedTools: calc.relatedTools ?? getRelatedCalculatorSlugs(calc),
  searchIntents: calc.searchIntents ?? buildSearchIntents(calc)
}));

export function getCalculatorConfig(slug: string): CalculatorConfig | undefined {
  return calculatorRegistry.find((c) => c.slug === slug);
}
